import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  AimOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseOutlined,
  CopyOutlined,
  DollarCircleOutlined,
  EnvironmentOutlined,
  FileDoneOutlined,
  IdcardOutlined,
  LoginOutlined,
  SearchOutlined,
  SendOutlined,
  ShareAltOutlined,
  SafetyCertificateOutlined,
  SolutionOutlined,
  ThunderboltOutlined,
  UserAddOutlined,
} from "@ant-design/icons";
import { AutoComplete, Button, Drawer, Input, Modal, Pagination, QRCode, Skeleton, Space, Tag, Typography, message } from "antd";
import { useLocation, useNavigate } from "react-router-dom";
import JobDetailsDrawer from "components/jobs/JobDetailsDrawer";
import OliveebProductAds from "components/promotions/OliveebProductAds";
import SignInRequiredModal from "components/auth/SignInRequiredModal";
import GooglePlacesAutocompleteInput from "common/GooglePlacesAutocompleteInput";
import { useAuth } from "contexts/AuthContext";
import paths from "routes/paths";
import { olreadiaService } from "services/olreadiaService";
import { bearerGuestToken, ensureGuestSession } from "utils/guestSession";
import { ensureGoogleMapsPlacesLoaded } from "utils/googleMapsLoader";
import { ensureClientId } from "utils/clientBootstrap";
import { OLIVEEB_COLORS } from "workspaces/colors";
import { API_BASE_URL, CLIENT_NAME_HEADER_KEY, CLIENT_NAME_HEADER_VALUE, O_API_KEY, O_CLIENT_APP } from "constants";
import apiClient from "utils/apiClient";
import { addressFieldsFromPlaceDetails } from "utils/googlePlaces";
import { hasRichText, richTextMarkup } from "./richTextUtils";

const { Text } = Typography;

const APP_SHARE_LINK = "https://olitask.com/download";
const DOWNLOAD_PAGE_LINK = "/download";
const APP_SHARE_MESSAGE =
  "Join me on Olitask - connect with trusted local helpers, post tasks quickly, track progress, and pay securely when the work is done. " +
  APP_SHARE_LINK;
const RESULTS_PAGE_SIZE = 6;
const LANDING_TASK_PAGE_SIZE = 20;
const DEFAULT_RADIUS_MILES = 10;
const LANDING_RADIUS_OPTIONS = [5, 10, 20, 30];
const LOCATION_TIMEOUT_MS = 10000;
const REQUEST_TIMEOUT_MS = 12000;
const uniqueSuggestionValues = (values) => {
  const seen = new Set();
  return values.reduce((items, value) => {
    const displayValue = String(value || "").trim().replace(/\s+/g, " ");
    const key = displayValue.toLowerCase();
    if (!displayValue || seen.has(key)) return items;
    seen.add(key);
    items.push(displayValue);
    return items;
  }, []);
};

const popularSearchLabel = (item) => {
  if (typeof item === "string") return item.trim();
  return String(item?.label || item?.searchQuery || item?.query || item?.name || "").trim();
};

const titleCase = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());

const popularLocationLabel = (item) => {
  if (typeof item === "string") return item.trim();

  const direct = String(item?.label || item?.location || item?.name || "").trim();
  if (direct) return direct;

  const city = titleCase(item?.city);
  const county = titleCase(item?.county);
  const state = String(item?.state || "").trim().toUpperCase();
  const country = String(item?.country || "").trim().toUpperCase();

  if (city && state) return `${city}, ${state}`;
  if (city && country) return `${city}, ${country}`;
  if (county && state) return `${county} County, ${state}`;
  if (state && country) return `${state}, ${country}`;
  return city || county || state || country;
};

const pageContent = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.content)) return data.content;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.jobOpportunities)) return data.jobOpportunities;
  if (Array.isArray(data?.opportunities)) return data.opportunities;
  return [];
};

const pageTotalElements = (data) => {
  if (typeof data?.totalElements === "number") return data.totalElements;
  if (typeof data?.total === "number") return data.total;
  if (typeof data?.totalCount === "number") return data.totalCount;
  return pageContent(data).length;
};

const renderedPageTotal = (data, items, page) => {
  const apiTotal = pageTotalElements(data);
  const pageOffset = Math.max(page - 1, 0) * RESULTS_PAGE_SIZE;
  const renderedCount = items.length;

  if (renderedCount < RESULTS_PAGE_SIZE) {
    return pageOffset + renderedCount;
  }

  return Math.max(apiTotal, pageOffset + renderedCount);
};

const firstText = (...values) => {
  for (const value of values) {
    if (typeof value === "string" && value.trim()) return value.trim();
    if (typeof value === "number") return String(value);
  }
  return "";
};

const googleMapsKeyFromConfigResponse = (response) => {
  const config = response?.data?.data || response?.data || {};
  return firstText(
    config.googleMapsApiKey,
    config.googleMapsApiMatrixKey,
    config.googleMapsApiBrowserKey,
    config.googleMapsKey,
    config.mapsApiKey,
  );
};

const namedRefLabel = (value) => {
  if (!value) return "";
  if (typeof value === "string") return titleCase(value);
  return firstText(value.name, value.label, value.title, value.code);
};

const toList = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  if (value instanceof Set) return Array.from(value);
  return [value];
};

const normalizeStrings = (value) =>
  toList(value)
    .map((item) => (item && typeof item === "object" ? namedRefLabel(item) : titleCase(item)))
    .filter(Boolean);

const compactList = (...groups) => groups.flat().filter(Boolean);

const firstValue = (...values) => {
  for (const value of values) {
    if (value !== undefined && value !== null && String(value).trim() !== "") return value;
  }
  return null;
};

const formatDate = (value) => {
  if (!value) return "Not set";
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return String(value);
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
};

const formatMoney = (value, currency = "USD") => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return String(value || "");
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency,
      maximumFractionDigits: Number.isInteger(numeric) ? 0 : 2,
    }).format(numeric);
  } catch {
    return `$${numeric.toLocaleString()}`;
  }
};

const locationSummaryOfTask = (task) => {
  const location = task?.location || {};
  if (typeof location === "string" && location.trim()) return location.trim();
  return firstText(
    location.address,
    location.formattedAddress,
    [location.city, location.state || location.country].filter(Boolean).join(", "),
    location.zipCode || location.zip,
    "Nearby",
  );
};

const amountLabelOfTask = (task) => {
  const charges = task?.taskCharges || {};
  const direct = firstValue(
    charges.tokenAmount,
    task?.tokenAmount,
    task?.amount,
    task?.budget,
    task?.compensation?.estimatedTotal,
  );
  const tip = Number(charges.tipAmount ?? task?.tipAmount ?? 0);
  const numeric = Number(direct);
  const currency = task?.currency || charges.currency || task?.compensation?.currency || "USD";

  if (Number.isFinite(numeric)) return formatMoney(numeric + (Number.isFinite(tip) ? tip : 0), currency);
  if (direct) return String(direct);
  return "Not listed";
};

const distanceLabelOfTask = (task) => {
  const value = firstValue(
    task?.drivingInfo?.distanceMiles,
    task?.distanceMiles,
    task?.distance,
  );
  const numeric = Number(value);
  if (Number.isFinite(numeric) && numeric < 999) {
    return `${numeric.toFixed(numeric < 10 ? 1 : 0)} mi away`;
  }
  if (typeof value === "string" && value.trim() && value !== "9999") return value;
  return "";
};

const taskPhotoOf = (task) =>
  task?.mainPhoto ||
  task?.photoUrl ||
  task?.photos?.[0]?.url ||
  task?.photos?.[0]?.fileUrl ||
  "https://placehold.co/600x420?text=Olitask";

const taskDescriptionPreview = (task) =>
  firstText(task?.description, task?.summary, "No description available.");

const formatDurationLabel = (value) => {
  if (value === undefined || value === null || String(value).trim() === "") return "N/A";
  const raw = String(value).trim();
  const numeric = raw.match(/(\d+(?:\.\d+)?)/);
  if (!numeric) return raw;
  const parsed = Number(numeric[1]);
  if (!Number.isFinite(parsed)) return raw;

  const containsHour = /\bhour|hr\b/i.test(raw);
  const containsMin = /\bminute|min\b/i.test(raw);
  if (containsHour && !containsMin) {
    const hours = Math.floor(parsed);
    const minutes = Math.round((parsed - hours) * 60);
    if (minutes <= 0) return `${hours} hr${hours === 1 ? "" : "s"}`;
    return `${hours} hr${hours === 1 ? "" : "s"} ${minutes} min`;
  }

  const minutes = Math.round(parsed);
  if (minutes <= 60) return `${minutes} min${minutes === 1 ? "" : "s"}`;
  const hours = Math.floor(minutes / 60);
  const remainder = minutes % 60;
  if (remainder === 0) return `${hours} hr${hours === 1 ? "" : "s"}`;
  return `${hours} hr${hours === 1 ? "" : "s"} ${remainder} min`;
};

const formatDateTime = (value) => {
  if (!value) return "N/A";
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return String(value);
  return date.toLocaleString();
};

const normalizeCity = (value) => {
  const city = String(value || "").trim();
  if (!city || /\d/.test(city) || /^[A-Z]{2}$/.test(city)) return "";
  return city;
};

const normalizeStateCode = (value) => {
  const state = String(value || "").trim();
  if (!state) return "";
  const match = state.match(/\b([A-Z]{2})\b/);
  if (match) return match[1];
  return state.length <= 32 && !/\d/.test(state) ? state : "";
};

const cityZipOfTask = (task) => {
  const location = task?.location || {};
  const address = String(
    location?.address ||
      location?.formattedAddress ||
      task?.address ||
      task?.formattedAddress ||
      "",
  ).trim();
  const zipField = String(location?.zipCode || location?.zip || task?.zipCode || "").trim();
  const zipFromAddress = (address.match(/\b\d{5}(?:-\d{4})?\b/) || [])[0] || "";
  const zip = zipField || zipFromAddress;
  let city = normalizeCity(location?.city) || normalizeCity(task?.city);
  let state = normalizeStateCode(location?.state || location?.stateCode || task?.state || task?.stateCode);

  if (!city && address) {
    const parts = address.split(",").map((part) => part.trim()).filter(Boolean);
    const zipPartIndex = parts.findIndex((part) => /\b\d{5}(?:-\d{4})?\b/.test(part));
    if (zipPartIndex > 0) {
      const cityStatePart = parts[zipPartIndex - 1].replace(/\b\d{5}(?:-\d{4})?\b/g, "").trim();
      const stateMatch = cityStatePart.match(/\b([A-Z]{2})$/);
      city = normalizeCity(cityStatePart.replace(/\b[A-Z]{2}$/, "").trim());
      if (!state && stateMatch) state = normalizeStateCode(stateMatch[1]);
    }
    if (!city) {
      city = normalizeCity(parts.find((part) =>
        !/\b\d{5}(?:-\d{4})?\b/.test(part) &&
        !/(street|st|avenue|ave|road|rd|boulevard|blvd|drive|dr|lane|ln|apt|unit)/i.test(part)
      ));
    }
    if (!state) {
      const stateZipPart = parts.find((part) => /\b[A-Z]{2}\s+\d{5}(?:-\d{4})?\b/.test(part));
      const stateMatch = stateZipPart?.match(/\b([A-Z]{2})\s+\d{5}(?:-\d{4})?\b/);
      if (stateMatch) state = normalizeStateCode(stateMatch[1]);
    }
  }

  const stateZip = [state, zip].filter(Boolean).join(" ");
  const parts = [city, stateZip].filter(Boolean);
  return parts.length ? parts.join(", ") : "Location not provided";
};

const taskPhotosOf = (task) => {
  const photos = [
    task?.mainPhoto,
    task?.photoUrl,
    ...toList(task?.photos).map((photo) => (typeof photo === "string" ? photo : firstText(photo?.url, photo?.fileUrl, photo?.uri))),
  ].filter((photo) => typeof photo === "string" && photo.trim());
  return Array.from(new Set(photos));
};

const coreTasksOf = (task) => {
  const categories = Array.isArray(task?.categories)
    ? task.categories.map((category) => namedRefLabel(category)).filter(Boolean)
    : [];
  const categoryNames = normalizeStrings(task?.categoryNames);
  return Array.from(new Set([...categories, ...categoryNames]));
};

const guestTaskProgressStages = (task) => {
  const status = String(task?.status || "NEW").toUpperCase();
  const base = ["NEW", "ACCEPTED", "NAVIGATING", "ARRIVED", "IN_PROGRESS", "COMPLETED"];
  if (status === "EXPIRED") return ["NEW", "EXPIRED", ...base.slice(1)];
  if (status === "CANCELLED") {
    const cancelStatus = String(task?.cancel?.cancelTaskStatus || "").toUpperCase();
    const cancelIndex = base.indexOf(cancelStatus);
    if (cancelIndex >= 0) return [...base.slice(0, cancelIndex + 1), "CANCELLED", ...base.slice(cancelIndex + 1)];
    return [...base, "CANCELLED"];
  }
  return base;
};

const guestTaskProgressIndex = (task, stages) => {
  const status = String(task?.status || "NEW").toUpperCase();
  if (status === "CANCELLED" || status === "EXPIRED") return Math.max(stages.indexOf(status), 0);
  const index = stages.indexOf(status);
  if ((status === "IN_PROGRESS" || status === "COMPLETED") && stages.includes("ARRIVED")) {
    return Math.max(index, stages.indexOf("IN_PROGRESS"));
  }
  return Math.max(index, 0);
};

const guestTaskStatusLabel = (status) => {
  const labels = {
    NEW: "New",
    ACCEPTED: "Accepted",
    NAVIGATING: "OTW",
    ARRIVED: "Arrived",
    IN_PROGRESS: "In progress",
    COMPLETED: "Done",
    CANCELLED: "Cancelled",
    EXPIRED: "Expired",
  };
  const normalized = String(status || "").toUpperCase();
  return labels[normalized] || titleCase(String(status || "").replace(/_/g, " "));
};

const normalizeNearbyTasks = (data) => pageContent(data).map((task) => ({
  ...task,
  id: task?.id || task?.taskId,
}));

const numericCoordinate = (...values) => {
  for (const value of values) {
    const numeric = Number(value);
    if (Number.isFinite(numeric)) return numeric;
  }
  return null;
};

const taskCoordinates = (task) => {
  const location = task?.location || {};
  const pickupLocation = task?.pickupLocation || {};
  const coordinates = task?.coordinates || location.coordinates || pickupLocation.coordinates || {};
  const latitude = numericCoordinate(
    task?.latitude,
    task?.lat,
    task?.taskLatitude,
    task?.locationLatitude,
    location.latitude,
    location.lat,
    pickupLocation.latitude,
    pickupLocation.lat,
    coordinates.latitude,
    coordinates.lat,
    Array.isArray(coordinates) ? coordinates[1] : null,
  );
  const longitude = numericCoordinate(
    task?.longitude,
    task?.lon,
    task?.lng,
    task?.taskLongitude,
    task?.locationLongitude,
    location.longitude,
    location.lon,
    location.lng,
    pickupLocation.longitude,
    pickupLocation.lon,
    pickupLocation.lng,
    coordinates.longitude,
    coordinates.lon,
    coordinates.lng,
    Array.isArray(coordinates) ? coordinates[0] : null,
  );

  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null;
  return { latitude, longitude };
};

const fetchWithTimeout = async (url, options = {}, timeoutMs = REQUEST_TIMEOUT_MS) => {
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(url, { ...options, signal: controller.signal });
  } finally {
    window.clearTimeout(timer);
  }
};

const fetchIpLocationEstimate = async () => {
  const response = await fetchWithTimeout("https://ipapi.co/json/", {}, LOCATION_TIMEOUT_MS);
  if (!response.ok) throw new Error("Unable to estimate location.");
  const data = await response.json();
  const latitude = numericCoordinate(data.latitude, data.lat);
  const longitude = numericCoordinate(data.longitude, data.lon, data.lng);
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
    throw new Error("Unable to estimate location.");
  }
  const label = compactList(
    data.city,
    data.region || data.region_code,
    data.country_name || data.country,
  ).join(", ");
  return {
    label: label || "your area",
    coords: { latitude, longitude },
  };
};

const dedupeById = (items) => {
  const seen = new Set();
  return (items || []).filter((item) => {
    const id = item?.id || item?.taskId;
    if (!id) return true;
    if (seen.has(id)) return false;
    seen.add(id);
    return true;
  });
};

const salaryLabel = (job) => {
  const direct = firstValue(job?.salaryExpectation, job?.salaryExpectations, job?.salaryRange, job?.compensation, job?.pay, job?.payRange);
  if (direct) return String(direct);
  const currency = job?.currency || job?.salaryCurrency || job?.payCurrency || "USD";
  const min = firstValue(job?.salaryMin, job?.minSalary, job?.minimumSalary, job?.payMin, job?.minPay);
  const max = firstValue(job?.salaryMax, job?.maxSalary, job?.maximumSalary, job?.payMax, job?.maxPay);
  const single = firstValue(job?.salary, job?.payRate, job?.hourlyRate);
  const cadence = titleCase(firstValue(job?.salaryPeriod, job?.payPeriod, job?.rateUnit, job?.payFrequency));
  const suffix = cadence ? ` / ${cadence.toLowerCase()}` : "";
  if (min && max) return `${formatMoney(min, currency)} - ${formatMoney(max, currency)}${suffix}`;
  if (min) return `From ${formatMoney(min, currency)}${suffix}`;
  if (max) return `Up to ${formatMoney(max, currency)}${suffix}`;
  if (single) return `${formatMoney(single, currency)}${suffix}`;
  return "Not set";
};

const statusColor = (status) => {
  const normalized = String(status || "").toUpperCase();
  if (normalized === "OPEN" || normalized === "ACTIVE") return "green";
  if (normalized === "DRAFT") return "gold";
  if (normalized === "CLOSED" || normalized === "ARCHIVED") return "red";
  return "blue";
};

const roleOptionsFor = (roles, query) => {
  const normalizedQuery = query.trim().toLowerCase();
  if (!normalizedQuery) return [];

  const seenRoleTitles = new Set();

  return roles
    .map((role, index) => {
      const title = String(role.title || "").toLowerCase();
      const synonyms = Array.isArray(role.synonyms)
        ? role.synonyms.map((value) => String(value || "").toLowerCase())
        : [];
      const description = String(role.description || "").toLowerCase();
      let rank = Number.POSITIVE_INFINITY;

      if (title === normalizedQuery) rank = 0;
      else if (title.startsWith(normalizedQuery)) rank = 1;
      else if (title.includes(normalizedQuery)) rank = 2;
      else if (synonyms.some((value) => value === normalizedQuery)) rank = 3;
      else if (synonyms.some((value) => value.startsWith(normalizedQuery))) rank = 4;
      else if (synonyms.some((value) => value.includes(normalizedQuery))) rank = 5;
      else if (description.includes(normalizedQuery)) rank = 6;

      return { role, index, rank };
    })
    .filter((item) => Number.isFinite(item.rank))
    .sort(
      (a, b) =>
        a.rank - b.rank ||
        String(a.role.title || "").localeCompare(String(b.role.title || "")) ||
        a.index - b.index,
    )
    .filter(({ role }) => {
      const title = String(role.title || "").trim().replace(/\s+/g, " ");
      const key = title.toLowerCase();
      if (!title || seenRoleTitles.has(key)) return false;
      seenRoleTitles.add(key);
      return true;
    })
    .slice(0, 8)
    .map(({ role }) => ({
      value: role.title,
      label: (
        <Space>
          <SolutionOutlined style={{ color: OLIVEEB_COLORS.oliveebGreen }} />
          <span>{role.title}</span>
        </Space>
      ),
    }));
};

const locationLabel = (location) => {
  if (!location) return "";
  if (typeof location === "string") return location;
  return firstText(
    location.label,
    location.name,
    location.address,
    location.formattedAddress,
    [location.city, location.state, location.country].filter(Boolean).join(", "),
  );
};

const jobCardFromApi = (job) => {
  const specializations = normalizeStrings(job?.specializations || job?.requiredSpecializations);
  const skills = uniqueSuggestionValues([
    ...normalizeStrings(job?.skills || job?.requiredSkills),
    ...normalizeStrings(job?.otherRequiredSkills),
    ...normalizeStrings(job?.requiredWorkAreas),
    ...normalizeStrings(job?.otherRequiredWorkAreas),
  ]);
  const locations = toList(job?.requiredLocations).map(locationLabel).filter(Boolean);
  const postedSource = job?.createdAt || job?.postedAt || job?.postStartDate;
  return {
    id: job?.id || job?.jobOpportunityId,
    title: job?.title || "Untitled job post",
    organization: namedRefLabel(job?.workspace) || namedRefLabel(job?.poster) || titleCase(job?.posterType) || "Employer post",
    location: locations[0] || titleCase(job?.locationType) || "",
    type: normalizeStrings(job?.workSchedules)[0] || titleCase(job?.workSchedule) || titleCase(job?.status || "Open"),
    badge: titleCase(job?.status || "Open"),
    postedDate: postedSource ? formatDate(postedSource) : "",
    specialization: specializations[0] || normalizeStrings(job?.requiredWorkAreas)[0] || "",
    summary: firstText(job?.summary, job?.description, job?.responsibilities, job?.requirements),
    skills: uniqueSuggestionValues([...specializations, ...skills]),
  };
};

const talentCardFromApi = (profile) => {
  const profileId = profile?.professionProfileId || profile?.profileId || profile?.id;
  const skills = uniqueSuggestionValues([
    ...normalizeStrings(profile?.skills || profile?.interestedServices),
    ...normalizeStrings(profile?.specializations || profile?.sectors),
  ]);
  const locations = normalizeStrings(profile?.locations || profile?.interestedLocations);
  return {
    id: profileId,
    title: firstText(profile?.displayName, profile?.name, profile?.fullName, profile?.profileName, profile?.title) || "Talent profile",
    organization: firstText(profile?.professionTitle, profile?.headline, profile?.profileTitle) || "Professional profile",
    location: locations[0] || firstText(profile?.location, profile?.city, profile?.state),
    type: profile?.availableNow ? "Available now" : titleCase(profile?.educationLevel) || "Open to opportunities",
    badge: profile?.isVerified ? "Identity verified" : profile?.isBackgroundChecked ? "Background checked" : "Profile",
    summary: firstText(profile?.profileSummary, profile?.summary, profile?.description, profile?.experience),
    skills,
  };
};

const LandingTalentDetailsDrawer = ({ profile, loading, open, onContact, onClose }) => {
  const skills = uniqueSuggestionValues([
    ...normalizeStrings(profile?.skills || profile?.interestedServices),
    ...normalizeStrings(profile?.specializations || profile?.sectors),
    ...normalizeStrings(profile?.services || profile?.serviceOfferings),
  ]);
  const locations = normalizeStrings(profile?.locations || profile?.interestedLocations);
  const displayName = firstText(
    profile?.displayName,
    profile?.name,
    profile?.fullName,
    profile?.profileName,
    profile?.title,
  );
  const headline = firstText(profile?.professionTitle, profile?.headline, profile?.profileTitle);
  const summary = firstText(
    profile?.profileSummary,
    profile?.summary,
    profile?.description,
    profile?.experience,
  );
  const detailItems = [
    {
      label: "Location",
      value: locations[0] || firstText(profile?.location, profile?.city, profile?.state) || "Not set",
      icon: <EnvironmentOutlined />,
    },
    {
      label: "Availability",
      value: profile?.availableNow ? "Available now" : "Open to opportunities",
      icon: <ClockCircleOutlined />,
    },
    {
      label: "Verification",
      value: profile?.isVerified
        ? "Identity verified"
        : profile?.isBackgroundChecked
          ? "Background checked"
          : "Profile",
      icon: <SafetyCertificateOutlined />,
    },
    {
      label: "Education",
      value: titleCase(profile?.educationLevel) || "Not set",
      icon: <IdcardOutlined />,
    },
  ];

  return (
    <Drawer
      open={open}
      onClose={onClose}
      placement="right"
      size="default"
      title={displayName || "Talent details"}
      className="olreadia-job-drawer"
      extra={
        profile ? (
          <Button
            type="primary"
            icon={<SendOutlined />}
            onClick={onContact}
            style={{
              background: OLIVEEB_COLORS.oliveebGreen,
              borderColor: OLIVEEB_COLORS.oliveebGreen,
              fontWeight: 900,
            }}
          >
            Contact
          </Button>
        ) : null
      }
    >
      {loading ? (
        <Skeleton active paragraph={{ rows: 5 }} />
      ) : !profile ? (
        <div className="olreadia-job-drawer-empty">
          <Text className="olreadia-empty-title">Talent details are not available.</Text>
        </div>
      ) : (
        <Space direction="vertical" size={9} className="olreadia-job-drawer-content">
          <div className="olreadia-job-drawer-hero">
            <Space wrap>
              <Tag color={profile?.isVerified ? "green" : "blue"}>
                {profile?.isVerified ? "Verified" : "Talent"}
              </Tag>
              {profile?.availableNow ? <Tag color="green">Available now</Tag> : null}
            </Space>
            <h2>{displayName || "Talent profile"}</h2>
            {headline ? <Text>{headline}</Text> : null}
          </div>

          <div className="olreadia-job-drawer-grid">
            {detailItems.map((item) => (
              <div key={item.label} className="olreadia-job-drawer-fact">
                <span>{item.icon}</span>
                <div>
                  <Text type="secondary">{item.label}</Text>
                  <strong>{item.value}</strong>
                </div>
              </div>
            ))}
          </div>

          {skills.length ? (
            <div className="olreadia-job-drawer-block">
              <Text type="secondary">Skills and services</Text>
              <Space wrap size={[4, 4]}>
                {skills.map((item) => <Tag key={item}>{item}</Tag>)}
              </Space>
            </div>
          ) : null}

          {locations.length ? (
            <div className="olreadia-job-drawer-block">
              <Text type="secondary">Preferred locations</Text>
              <Space wrap size={[4, 4]}>
                {locations.map((item) => <Tag key={item}>{item}</Tag>)}
              </Space>
            </div>
          ) : null}

          {hasRichText(summary) ? (
            <div className="olreadia-job-drawer-section">
              <Text type="secondary">Summary</Text>
              <div className="olreadia-rich-text" dangerouslySetInnerHTML={richTextMarkup(summary)} />
            </div>
          ) : null}
        </Space>
      )}
    </Drawer>
  );
};

const GuestTaskDetailsDrawer = ({ task, open, onClose, onLogin, onSignup, onRequireAccount }) => {
  const status = String(task?.status || "NEW").toUpperCase();
  const distance = distanceLabelOfTask(task);
  const photos = taskPhotosOf(task);
  const mainPhoto = photos[0] || taskPhotoOf(task);
  const galleryPhotos = photos.slice(1);
  const coreTasks = coreTasksOf(task);
  const stages = guestTaskProgressStages(task);
  const currentIndex = guestTaskProgressIndex(task, stages);
  const postedAt = formatDateTime(firstValue(task?.createdAt, task?.createdAtUtc, task?.createdOn));

  return (
    <Drawer
      title={null}
      placement="right"
      size="large"
      open={open}
      onClose={onClose}
      closable={false}
      destroyOnHidden
      className="olreadia-guest-task-drawer"
    >
      {task ? (
        <div className="olreadia-guest-task">
          <div className="olreadia-guest-task-topbar">
            <Button type="text" shape="circle" onClick={onClose} icon={<CloseOutlined />} />
            <strong>Task Details</strong>
            <span />
          </div>

          <div
            className="my-requests-status-progress"
            aria-label={`Task status ${guestTaskStatusLabel(status)}`}
            style={{
              "--task-stage-count": stages.length,
              "--task-line-count": Math.max(1, stages.length - 1),
            }}
          >
            <div className="my-requests-status-progress__line">
              {stages.slice(0, -1).map((stage, index) => (
                <span
                  key={`${stage}-line`}
                  className={index < currentIndex ? "is-active" : ""}
                />
              ))}
            </div>
            <div className="my-requests-status-progress__steps">
              {stages.map((stage, index) => {
                const isComplete = index < currentIndex;
                const isCurrent = index === currentIndex;
                const isTerminal = stage === "CANCELLED" || stage === "EXPIRED";
                return (
                  <div
                    key={stage}
                    className={[
                      "my-requests-status-progress__step",
                      index > 0 && index <= currentIndex ? "is-line-before-active" : "",
                      index < currentIndex ? "is-line-after-active" : "",
                    ].filter(Boolean).join(" ")}
                  >
                    <span
                      className={[
                        "my-requests-status-progress__circle",
                        isComplete ? "is-completed" : "",
                        isCurrent ? "is-current" : "",
                        isTerminal ? `is-${stage.toLowerCase()}` : "",
                      ].filter(Boolean).join(" ")}
                    >
                      {isComplete || isCurrent ? (isTerminal ? "x" : "✓") : ""}
                    </span>
                    <strong>{guestTaskStatusLabel(stage)}</strong>
                  </div>
                );
              })}
            </div>
          </div>

          <section className="olreadia-guest-task-summary">
            <span className="olreadia-guest-task-glow" />
            <h2>{firstText(task?.title, task?.name, "Help Request")}</h2>
            <div className="olreadia-guest-task-head">
              <Tag>{guestTaskStatusLabel(status)}</Tag>
              {distance ? <Tag>{distance}</Tag> : null}
            </div>

            <div className="olreadia-guest-task-metrics">
              <span>
                <DollarCircleOutlined />
                <span>
                  <Text className="olreadia-guest-task-label">Amount Offered</Text>
                  <Text className="olreadia-guest-task-value is-offered">{amountLabelOfTask(task)}</Text>
                </span>
              </span>
              <span>
                <ClockCircleOutlined />
                <span>
                  <Text className="olreadia-guest-task-label">Estimated Task Duration (ETD)</Text>
                  <Text className="olreadia-guest-task-value">
                    {formatDurationLabel(firstValue(task?.requesterDuration, task?.duration, task?.estimatedDuration))}
                  </Text>
                </span>
              </span>
              <span>
                <EnvironmentOutlined />
                <span>
                  <Text className="olreadia-guest-task-label">Task Location</Text>
                  <Text className="olreadia-guest-task-value">{cityZipOfTask(task)}</Text>
                </span>
              </span>
              <span>
                <CalendarOutlined />
                <span>
                  <Text className="olreadia-guest-task-label">Posted At</Text>
                  <Text className="olreadia-guest-task-value">{postedAt}</Text>
                </span>
              </span>
            </div>

            <Button
              type="primary"
              size="large"
              block
              className="olreadia-guest-task-primary"
              onClick={onRequireAccount}
            >
              I'd Like to Help
            </Button>
          </section>

          <section className="olreadia-guest-task-card">
            <h3>Task Description</h3>
            <p className="olreadia-guest-task-desc">{taskDescriptionPreview(task)}</p>
          </section>

          {mainPhoto ? (
            <div className="olreadia-guest-task-photo">
              <img src={mainPhoto} alt="" />
            </div>
          ) : null}

          {coreTasks.length ? (
            <section className="olreadia-guest-task-card">
              <div className="olreadia-guest-task-section-head">
                <FileDoneOutlined />
                <h3>Core Tasks</h3>
              </div>
              <div className="olreadia-guest-task-chips">
                {coreTasks.map((name, index) => (
                  <span key={`${name}-${index}`}>
                    <CheckCircleOutlined />
                    {name}
                  </span>
                ))}
              </div>
            </section>
          ) : null}

          {galleryPhotos.length ? (
            <section className="olreadia-guest-task-card">
              <h3>Photo Gallery</h3>
              <div className="olreadia-guest-task-gallery">
                {galleryPhotos.map((photo, index) => (
                  <img key={`${photo}-${index}`} src={photo} alt="" />
                ))}
              </div>
            </section>
          ) : null}

          <section className="olreadia-guest-task-locked">
            <span className="olreadia-modal-badge">Account Required</span>
            <h3>Continue with an account</h3>
            <p>
              To respond to this task and start helping, please login or create
              an account.
            </p>
            <div className="olreadia-guest-task-actions">
              <Button size="large" onClick={onLogin} icon={<LoginOutlined />}>
                Login
              </Button>
              <Button size="large" type="primary" onClick={onSignup} icon={<UserAddOutlined />}>
                Create Account
              </Button>
            </div>
          </section>
        </div>
      ) : (
        <Skeleton active paragraph={{ rows: 8 }} />
      )}
    </Drawer>
  );
};

const GuestMapTaskSummaryModal = ({ task, open, onClose, onOpenDetails }) => {
  const status = String(task?.status || "NEW").toUpperCase();
  const duration = formatDurationLabel(firstValue(task?.requesterDuration, task?.duration, task?.estimatedDuration));

  return (
    <Modal
      centered
      open={open}
      footer={null}
      width={340}
      onCancel={onClose}
      className="olreadia-map-task-summary-modal"
      destroyOnClose
    >
      {task ? (
        <div className="olreadia-map-task-summary">
          <div className="olreadia-map-task-summary__top">
            <h3>{firstText(task?.title, task?.name, "Help Request")}</h3>
            <span className="olreadia-map-task-summary__status">
              <i />
              {guestTaskStatusLabel(status)}
            </span>
          </div>
          <p>{taskDescriptionPreview(task)}</p>
          <div className="olreadia-map-task-summary__meta">
            <span>
              <small>OFFER</small>
              <strong>{amountLabelOfTask(task)}</strong>
            </span>
            <span>
              <small>DURATION</small>
              <strong>{duration}</strong>
            </span>
          </div>
          <Button
            type="primary"
            block
            className="olreadia-map-task-summary__button"
            onClick={() => onOpenDetails?.(task)}
          >
            VIEW TASK DETAILS →
          </Button>
        </div>
      ) : null}
    </Modal>
  );
};

const tokens = {
  bg: "#F6FBF9",
  card: "#FFFFFF",
  border: "#E2E8F0",
  green: OLIVEEB_COLORS.oliveebGreen,
  greenSoft: OLIVEEB_COLORS.oliveebGreenSoft,
  darkGreen: OLIVEEB_COLORS.oliveebDarkGreen,
  blue: OLIVEEB_COLORS.oliveebBlue,
  blueSoft: OLIVEEB_COLORS.oliveebBlueSoft,
  darkBlue: OLIVEEB_COLORS.oliveebDarkBlue,
  goldSoft: OLIVEEB_COLORS.goldSoft,
  goldBorder: OLIVEEB_COLORS.goldBorder,
  red: OLIVEEB_COLORS.oliveebRed,
  darkRed: OLIVEEB_COLORS.oliveebDarkRed,
  text: "#0F172A",
  muted: "#475569",
};

const landingCss = `
  .olreadia-landing {
    position: relative;
    min-height: 100vh;
    background: #F7FAFC;
    color: ${tokens.text};
    overflow-x: hidden;
  }

  .olreadia-landing section {
    padding-top: 8px;
    padding-bottom: 0;
  }

  .olreadia-landing::before {
    content: "";
    position: fixed;
    inset: 0;
    pointer-events: none;
    background: linear-gradient(180deg, rgba(255,255,255,0.72) 0%, rgba(255,255,255,0) 34%);
  }

  .olreadia-landing > * {
    position: relative;
    z-index: 1;
  }

  .olreadia-landing-shell {
    width: min(1380px, calc(100% - 36px));
    margin: 0 auto;
    padding: 16px 0 30px;
  }

  .olreadia-landing-header {
    position: relative;
    display: grid;
    grid-template-columns: minmax(320px, 520px) minmax(0, 1fr);
    align-items: stretch;
    gap: 22px;
    text-align: left;
    padding: 0 0 22px;
  }

  .olreadia-welcome-brand {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    width: min(44vw, 520px);
    max-width: 100%;
    aspect-ratio: 1672 / 941;
    padding: 0;
    border: 0;
    border-radius: 14px;
    background: transparent;
    cursor: pointer;
    overflow: hidden;
  }

  .olreadia-welcome-brand img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .olreadia-header-content {
    display: flex;
    min-width: 0;
    flex-direction: column;
    align-items: flex-end;
    justify-content: space-between;
    gap: 18px;
    height: 100%;
    padding: 6px 0;
  }

  .olreadia-promo-row {
    width: min(100%, 620px);
    min-width: 0;
    flex: 1;
    margin: 0;
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) 134px;
    align-items: stretch;
    gap: 14px;
    padding: 18px;
    border: 1px solid rgba(16, 185, 129, 0.18);
    border-radius: 16px;
    background:
      linear-gradient(135deg, rgba(255,255,255,0.96) 0%, rgba(239,251,255,0.94) 52%, rgba(237,253,245,0.96) 100%);
    box-shadow: 0 16px 34px rgba(15, 23, 42, 0.08), inset 0 1px 0 rgba(255,255,255,0.86);
    position: relative;
    overflow: hidden;
  }

  .olreadia-promo-row.is-compact {
    width: min(100%, 880px);
    grid-template-columns: minmax(220px, 1fr) 128px minmax(260px, 1.05fr);
    padding: 12px;
    align-items: center;
  }

  .olreadia-promo-row .pm-right-ads-section {
    position: relative;
    width: 100%;
    min-width: 0;
    max-width: 100%;
    align-self: stretch;
    justify-self: stretch;
    box-sizing: border-box;
    border-color: #d6f0e1;
    border-radius: 12px;
    box-shadow: 0 7px 18px rgba(15, 23, 42, 0.05);
    overflow: hidden;
  }

  .olreadia-promo-row .pm-right-ads-section *,
  .olreadia-promo-row .pm-right-ads-section *::before,
  .olreadia-promo-row .pm-right-ads-section *::after {
    box-sizing: border-box;
  }

  .olreadia-promo-row .pm-right-ads-section .oliveeb-product-ad {
    align-items: start;
    gap: 7px;
    grid-template-columns: minmax(0, 1fr);
    justify-items: start;
    min-height: 0;
    min-width: 0;
    max-width: 100%;
    overflow: hidden;
    padding: 9px;
    width: 100%;
  }

  .olreadia-promo-row .pm-right-ads-section .oliveeb-product-ad__icon {
    width: 100%;
    height: 46px;
  }

  .olreadia-promo-row .pm-right-ads-section .oliveeb-product-ad__icon img {
    height: 36px;
    max-height: 36px;
  }

  .olreadia-promo-row .pm-right-ads-section .oliveeb-product-ad__copy {
    display: block;
    max-width: 100%;
    min-width: 0;
    overflow: hidden;
    width: 100%;
  }

  .olreadia-promo-row .pm-right-ads-section .oliveeb-product-ad__title {
    display: block;
    font-size: 14px;
    line-height: 1.12;
    max-width: 100%;
    overflow-wrap: anywhere;
    white-space: normal;
    word-break: break-word;
  }

  .olreadia-promo-row .pm-right-ads-section .oliveeb-product-ad__text {
    display: block;
    font-size: 11px;
    line-height: 1.26;
    margin-top: 3px;
    max-width: 100%;
    overflow: hidden;
    overflow-wrap: anywhere;
    white-space: normal;
    word-break: break-word;
  }

  .olreadia-promo-row .pm-right-ads-section .oliveeb-product-ad__link {
    display: inline;
    max-width: 100%;
    overflow-wrap: anywhere;
    white-space: normal;
    word-break: break-word;
  }

  .olreadia-promo-row .pm-right-ads-section .oliveeb-product-ads__label {
    font-size: 8px;
    padding: 3px 6px;
  }

  .olreadia-promo-row::before {
    content: "";
    position: absolute;
    inset: 0;
    pointer-events: none;
    background: linear-gradient(115deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.62) 42%, rgba(255,255,255,0) 68%);
    transform: translateX(-42%);
  }

  .olreadia-subhead-audience {
    position: relative;
    display: grid;
    align-items: start;
    align-content: start;
    gap: 5px;
    margin: 0;
    color: ${tokens.blue};
    font-size: 16px;
    line-height: 1.34;
    font-style: normal;
    font-weight: 500;
    text-align: left;
    text-shadow: 0 1px 0 rgba(255,255,255,0.75);
    min-width: 0;
  }

  .olreadia-subhead-label {
    color: ${tokens.green};
    font-size: 12px;
    line-height: 1;
    font-family: inherit;
    font-style: normal;
    font-weight: 900;
    letter-spacing: 0;
    text-transform: uppercase;
  }

  .olreadia-promo-button {
    position: relative;
    width: 100%;
    min-width: 0;
    min-height: 38px;
    height: auto;
    align-self: center;
    justify-self: stretch;
    border-radius: 999px;
    padding: 0 10px;
    font-size: 12px;
    font-weight: 900;
    box-shadow: none;
    white-space: normal;
  }

  .olreadia-promo-cta {
    position: relative;
    display: grid;
    align-content: center;
    gap: 14px;
    min-width: 0;
  }

  .olreadia-header-share-button {
    width: fit-content;
    min-width: 120px;
    height: 34px;
    margin-top: 6px;
    border-radius: 999px;
    color: ${tokens.green};
    background: ${tokens.greenSoft};
    border-color: ${tokens.green};
    font-size: 12px;
    font-weight: 900;
  }

  .olreadia-header-share-button:hover,
  .olreadia-header-share-button:focus {
    color: ${tokens.green};
    background: ${tokens.greenSoft};
    border-color: ${tokens.green};
  }

  .olreadia-download-button {
    display: block;
    min-width: 0;
    width: 100%;
    aspect-ratio: 430 / 409;
    padding: 0;
    border-radius: 10px;
    border: 1px solid rgba(0, 128, 255, 0.18);
    background: #FFFFFF;
    box-shadow: 0 10px 20px rgba(0, 58, 112, 0.1);
    overflow: hidden;
  }

  .olreadia-download-button img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  .olreadia-header-actions {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 10px;
    min-width: 0;
  }

  .olreadia-action-row {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    flex-wrap: wrap;
    gap: 8px;
  }

  .olreadia-public-nav {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 2px;
  }

  .olreadia-public-nav button,
  .olreadia-public-nav a {
    min-height: 32px;
    border-radius: 999px;
    border: 1px solid #D7EAFE;
    background: #FFFFFF;
    color: ${tokens.darkBlue};
    padding: 5px 11px;
    font-size: 12px;
    font-weight: 900;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    text-decoration: none;
  }

  .olreadia-public-nav button.is-active {
    border-color: ${tokens.green};
    background: ${tokens.green};
    color: #FFFFFF;
  }

  .olreadia-public-nav a {
    border-color: rgba(0, 128, 255, 0.24);
    color: ${tokens.blue};
  }

  .olreadia-public-nav a:hover {
    border-color: ${tokens.blue};
    color: ${tokens.blue};
  }

  .olreadia-mobile-nearby-button {
    display: none;
  }

  .olreadia-pill-button {
    min-width: 112px;
    height: 42px;
    border-radius: 999px;
    font-size: 14px;
    font-weight: 900;
  }

  .olreadia-side-cta-row {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 6px;
    margin-bottom: 2px;
  }

  .olreadia-side-cta-row .olreadia-pill-button {
    width: 100%;
    min-width: 0;
    height: 40px;
    padding: 0 8px;
    font-size: 12px;
  }

  .olreadia-main-grid {
    display: grid;
    grid-template-columns: minmax(0, 1.65fr) minmax(300px, 0.72fr);
    gap: 10px;
    align-items: stretch;
    margin-top: 10px;
  }

  .olreadia-main-grid.is-home {
    grid-template-columns: 1fr;
  }

  .olreadia-panel {
    background: #FFFFFF;
    border: 1px solid ${tokens.border};
    border-radius: 12px;
    box-shadow: 0 12px 30px rgba(15,23,42,0.07);
  }

  .olreadia-discovery {
    display: flex;
    flex-direction: column;
    min-width: 0;
    padding: 18px;
  }

  .olreadia-main-grid.is-home .olreadia-discovery {
    padding: 0;
    border: 0;
    background: transparent;
    box-shadow: none;
  }

  .olreadia-eyebrow {
    display: inline-flex;
    align-items: center;
    margin: 0 0 12px;
    padding: 8px 14px;
    border-radius: 999px;
    border: 1px solid rgba(39, 174, 48, 0.22);
    background: linear-gradient(135deg, #FFFFFF 0%, ${tokens.greenSoft} 100%);
    color: ${tokens.green};
    font-size: 18px;
    line-height: 1.15;
    font-weight: 950;
    box-shadow: 0 10px 22px rgba(39,174,48,0.12);
  }

  .olreadia-toggle {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 6px;
    padding: 5px;
    border-radius: 10px;
    border: 1px solid #E2E8F0;
    background: #F8FAFC;
    box-shadow: none;
    margin-bottom: 14px;
  }

  .olreadia-toggle button {
    min-height: 40px;
    border: 1px solid transparent;
    border-radius: 8px;
    background: transparent;
    color: ${tokens.muted};
    font-size: 13px;
    font-weight: 900;
    cursor: pointer;
  }

  .olreadia-toggle button.is-active {
    color: #FFFFFF;
    background: ${tokens.green};
    border-color: #8DE99A;
    box-shadow: 0 6px 12px rgba(39,174,48,0.12);
  }

  .olreadia-toggle.is-job button.is-active {
    background: ${tokens.blue};
    border-color: ${tokens.blue};
    box-shadow: 0 6px 12px rgba(35,133,219,0.14);
  }

  .olreadia-discovery-heading {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 12px;
    margin-bottom: 14px;
  }

  .olreadia-discovery-heading .olreadia-section-title {
    margin: 0;
  }

  .olreadia-discovery-heading .olreadia-toggle {
    flex: 0 0 280px;
    margin-bottom: 0;
  }

  .olreadia-section-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 8px;
  }

  .olreadia-chip-label {
    display: inline-flex;
    align-items: center;
    min-height: 24px;
    border-radius: 999px;
    padding: 4px 9px;
    color: ${tokens.blue};
    background: ${tokens.blueSoft};
    border: 1px solid #D7EAFE;
    font-size: 10px;
    font-weight: 900;
    text-transform: uppercase;
  }

  .olreadia-section-title {
    margin: 6px 0 0;
    margin-bottom: 10px;
    color: ${tokens.green};
    font-size: 22px;
    line-height: 1.2;
    font-weight: 900;
  }

  .olreadia-section-text {
    margin: 0 0 12px;
    color: ${tokens.muted};
    font-size: 14px;
    line-height: 1.45;
    font-weight: 400;
  }

  .olreadia-search-box {
    position: relative;
    border: 1px solid rgba(0, 128, 255, 0.18);
    background:
      linear-gradient(135deg, rgba(255,255,255,0.98) 0%, rgba(244,251,255,0.96) 48%, rgba(241,255,247,0.96) 100%);
    border-radius: 16px;
    padding: 14px;
    margin-bottom: 12px;
    box-shadow: 0 18px 42px rgba(0, 58, 112, 0.1), inset 0 1px 0 rgba(255,255,255,0.92);
    overflow: hidden;
  }

  .olreadia-search-box::before {
    content: "";
    position: absolute;
    inset: 0;
    pointer-events: none;
    background: linear-gradient(118deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.72) 43%, rgba(255,255,255,0) 68%);
    transform: translateX(-38%);
  }

  .olreadia-search-grid {
    position: relative;
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(150px, 0.72fr);
    gap: 10px;
  }

  .olreadia-search-grid .ant-select,
  .olreadia-search-grid .ant-select-selector {
    min-width: 0;
  }

  .olreadia-search-field {
    position: relative;
    min-width: 0;
  }

  .olreadia-search-field.ant-input-affix-wrapper {
    min-height: 52px;
    border-radius: 999px;
    border: 1px solid rgba(0, 128, 255, 0.22);
    background: rgba(255,255,255,0.94);
    box-shadow: 0 12px 26px rgba(0, 58, 112, 0.1), inset 0 1px 0 rgba(255,255,255,0.95);
    transition: border-color 160ms ease, box-shadow 160ms ease, transform 160ms ease;
  }

  .olreadia-search-field.is-location.ant-input-affix-wrapper {
    border-color: rgba(16, 185, 129, 0.24);
  }

  .olreadia-search-field.ant-input-affix-wrapper:hover,
  .olreadia-search-field.ant-input-affix-wrapper-focused {
    border-color: ${tokens.blue} !important;
    box-shadow: 0 0 0 4px rgba(0,128,255,0.1), 0 16px 32px rgba(0, 58, 112, 0.14), inset 0 1px 0 rgba(255,255,255,0.98) !important;
    transform: translateY(-1px);
  }

  .olreadia-search-field.ant-input-affix-wrapper .ant-input,
  .olreadia-search-field.ant-input-affix-wrapper .ant-input::placeholder {
    font-size: 15px;
    font-weight: 800;
  }

  .olreadia-search-field.ant-input-affix-wrapper .ant-input::placeholder {
    color: #64748B;
  }

  .olreadia-search-field.ant-input-affix-wrapper .ant-input-prefix {
    color: ${tokens.green};
    margin-inline-end: 10px;
    font-size: 17px;
  }

  .olreadia-search-field.is-location.ant-input-affix-wrapper .ant-input-prefix {
    color: ${tokens.blue};
  }

  .olreadia-search-clear {
    color: ${tokens.muted};
    border-radius: 999px;
  }

  .olreadia-search-actions {
    position: relative;
    display: grid;
    grid-template-columns: 1fr;
    gap: 8px;
    margin-top: 10px;
  }

  .olreadia-search-submit {
    overflow: hidden;
    border-radius: 999px !important;
    border: 0 !important;
    background: linear-gradient(135deg, ${tokens.blue} 0%, ${tokens.green} 100%) !important;
    box-shadow: 0 14px 30px rgba(0, 128, 255, 0.22), inset 0 1px 0 rgba(255,255,255,0.28);
    transition: transform 160ms ease, box-shadow 160ms ease;
  }

  .olreadia-search-submit:hover {
    transform: translateY(-1px);
    box-shadow: 0 18px 36px rgba(0, 128, 255, 0.28), inset 0 1px 0 rgba(255,255,255,0.32);
  }

  .olreadia-search-submit span:last-child {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .olreadia-suggestion-block {
    margin-top: 12px;
  }

  .olreadia-search-dropdown {
    border-radius: 12px;
    padding: 6px;
    box-shadow: 0 18px 36px rgba(15,23,42,0.15);
  }

  .olreadia-suggestion-label {
    display: block;
    margin: 0 0 6px;
    color: ${tokens.muted};
    font-size: 9px;
    font-weight: 700;
    text-transform: uppercase;
    white-space: nowrap;
  }

  .olreadia-suggestion-label.is-spaced {
    margin-top: 10px;
  }

  .olreadia-chip-row {
    display: flex;
    flex-wrap: nowrap;
    gap: 6px;
    overflow-x: auto;
    padding-bottom: 2px;
  }

  .olreadia-suggestion-chip {
    border: 1px solid #D6F0E1;
    border-radius: 999px;
    background: #FFFFFF;
    color: ${tokens.darkGreen};
    min-height: 30px;
    padding: 5px 10px;
    font-size: 11px;
    font-weight: 600;
    white-space: nowrap;
    flex: 0 0 auto;
    cursor: pointer;
  }

  .olreadia-suggestion-chip.is-location {
    border-color: #BBDDF8;
    background: ${tokens.blueSoft};
    color: ${tokens.blue};
  }

  .olreadia-suggestion-chip.is-active {
    background: ${tokens.green};
    border-color: ${tokens.green};
    color: #FFFFFF;
  }

  .olreadia-suggestion-chip.is-job.is-active,
  .olreadia-suggestion-chip.is-location.is-active {
    background: ${tokens.blue};
    border-color: ${tokens.blue};
  }

  .olreadia-signals {
    display: grid;
    gap: 8px;
  }

  .olreadia-signal {
    display: grid;
    grid-template-columns: 38px 1fr;
    gap: 10px;
    align-items: center;
    min-height: 54px;
    padding: 9px 12px;
    border-radius: 10px;
    border: 1px solid #D6F0E1;
    background: #F7FFF9;
  }

  .olreadia-signal-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border-radius: 10px;
    font-size: 15px;
    color: ${tokens.darkGreen};
    background: ${tokens.greenSoft};
  }

  .olreadia-signal-title {
    display: block;
    color: ${tokens.darkGreen};
    font-size: 13px;
    font-weight: 900;
  }

  .olreadia-signal-text {
    display: block;
    color: ${tokens.muted};
    font-size: 12px;
    line-height: 1.35;
    font-weight: 400;
  }

  .olreadia-empty-state {
    padding: 14px;
    border-radius: 10px;
    border: 1px solid ${tokens.border};
    background: #F8FAFC;
  }

  .olreadia-empty-title {
    display: block;
    color: ${tokens.blue};
    font-size: 15px;
    font-weight: 900;
  }

  .olreadia-empty-text {
    display: block;
    margin-top: 4px;
    color: ${tokens.darkRed};
    font-size: 13px;
    line-height: 1.45;
  }

  .olreadia-empty-help-line {
    display: block;
    margin-top: 8px;
    color: ${tokens.muted};
    line-height: 1.5;
  }

  .olreadia-results-panel {
    margin-top: 14px;
    display: grid;
    gap: 10px;
  }

  .olreadia-results-head {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    gap: 10px;
    flex-wrap: wrap;
  }

  .olreadia-results-title {
    display: block;
    color: ${tokens.text};
    font-size: 18px;
    line-height: 1.25;
    font-weight: 900;
  }

  .olreadia-results-title.is-talent {
    color: ${tokens.blue};
  }

  .olreadia-results-title.is-opportunity {
    color: ${tokens.green};
  }

  .olreadia-results-note {
    display: block;
    margin-top: 4px;
    color: ${tokens.muted};
    font-size: 12px;
    line-height: 1.45;
    font-weight: 400;
  }

  .olreadia-result-count {
    color: ${tokens.blue};
    background: ${tokens.blueSoft};
    border: 1px solid #D7EAFE;
    border-radius: 999px;
    padding: 5px 10px;
    font-size: 11px;
    font-weight: 900;
  }

  .olreadia-result-list {
    display: grid;
    gap: 8px;
  }

  .olreadia-results-pagination {
    display: flex;
    justify-content: flex-end;
    padding-top: 2px;
  }

  .olreadia-results-pagination .ant-pagination-item,
  .olreadia-results-pagination .ant-pagination-prev,
  .olreadia-results-pagination .ant-pagination-next {
    min-width: 30px;
    height: 30px;
    line-height: 28px;
  }

  .olreadia-result-card {
    display: grid;
    grid-template-columns: 1fr auto;
    gap: 12px;
    align-items: center;
    padding: 12px;
    border-radius: 10px;
    border: 1px solid #D6F0E1;
    background: #FFFFFF;
    cursor: pointer;
    transition: border-color 160ms ease, box-shadow 160ms ease;
  }

  .olreadia-result-card > div {
    min-width: 0;
  }

  .olreadia-result-actions {
    display: flex;
    gap: 8px;
    align-items: center;
    justify-content: flex-end;
    flex-wrap: wrap;
  }

  .olreadia-result-card:hover {
    border-color: ${tokens.green};
    box-shadow: 0 8px 18px rgba(15,23,42,0.07);
  }

  .olreadia-result-card.is-job {
    border-color: #D7EAFE;
  }

  .olreadia-result-meta {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-bottom: 6px;
  }

  .olreadia-result-pill {
    min-height: 24px;
    max-width: 100%;
    display: inline-flex;
    align-items: center;
    border-radius: 999px;
    padding: 3px 8px;
    color: ${tokens.green};
    background: ${tokens.greenSoft};
    font-size: 11px;
    font-weight: 900;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .olreadia-result-card.is-job .olreadia-result-pill {
    color: ${tokens.blue};
    background: ${tokens.blueSoft};
  }

  .olreadia-job-drawer .ant-drawer-header {
    border-bottom-color: #E2E8F0;
    padding: 9px 12px;
  }

  .olreadia-job-drawer .ant-drawer-title {
    color: ${tokens.green};
    font-size: 14px;
    font-weight: 900;
  }

  .olreadia-job-drawer .ant-drawer-extra {
    flex: 1;
    display: flex;
    justify-content: flex-end;
    margin-inline-start: 8px;
  }

  .olreadia-job-drawer-header-actions {
    width: 100%;
    justify-content: flex-end;
    overflow-x: auto;
    white-space: nowrap;
  }

  .olreadia-job-drawer .ant-drawer-body {
    padding: 10px 12px;
  }

  .olreadia-job-drawer-content {
    width: 100%;
  }

  .olreadia-job-drawer-empty {
    display: flex;
    min-height: 160px;
    align-items: center;
    justify-content: center;
    text-align: center;
  }

  .olreadia-job-drawer-hero {
    display: grid;
    gap: 4px;
    padding: 9px;
    border: 1px solid #D6F0E1;
    border-radius: 10px;
    background: linear-gradient(150deg, #FFFFFF, #F3FCF7);
  }

  .olreadia-job-drawer-hero .ant-tag,
  .olreadia-job-drawer-block .ant-tag {
    margin-inline-end: 0;
    font-size: 11px;
    line-height: 18px;
  }

  .olreadia-job-drawer-hero h2 {
    margin: 0;
    color: ${tokens.green};
    font-size: 17px;
    line-height: 1.18;
    font-weight: 950;
  }

  .olreadia-job-drawer-hero .ant-typography {
    font-size: 12px;
  }

  .olreadia-job-drawer-link-row {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 6px;
  }

  .olreadia-job-drawer-link-row .ant-btn {
    border-radius: 8px;
    font-weight: 900;
  }

  .olreadia-job-drawer-bottom-actions {
    display: flex;
    flex-wrap: nowrap;
    gap: 8px;
    justify-content: flex-end;
    overflow-x: auto;
    padding-top: 4px;
  }

  .olreadia-job-drawer-bottom-actions .ant-btn {
    border-radius: 8px;
    font-weight: 900;
  }

  .olreadia-job-drawer-details-button.ant-btn,
  .olreadia-result-details-button.ant-btn {
    background: #FFFFFF;
    border-color: ${tokens.blue};
    color: ${tokens.blue};
  }

  .olreadia-job-drawer-share-button.ant-btn,
  .olreadia-result-share-button.ant-btn {
    background: #FFFFFF;
    border-color: ${tokens.green};
    color: ${tokens.green};
  }

  .olreadia-job-drawer-details-button.ant-btn:hover,
  .olreadia-job-drawer-details-button.ant-btn:focus,
  .olreadia-result-details-button.ant-btn:hover,
  .olreadia-result-details-button.ant-btn:focus {
    background: ${tokens.blueSoft};
    border-color: ${tokens.blue};
    color: ${tokens.blue};
  }

  .olreadia-job-drawer-share-button.ant-btn:hover,
  .olreadia-job-drawer-share-button.ant-btn:focus,
  .olreadia-result-share-button.ant-btn:hover,
  .olreadia-result-share-button.ant-btn:focus {
    background: ${tokens.greenSoft};
    border-color: ${tokens.green};
    color: ${tokens.green};
  }

  .olreadia-job-drawer-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 6px;
  }

  .olreadia-job-drawer-fact {
    display: grid;
    grid-template-columns: 24px minmax(0, 1fr);
    gap: 6px;
    align-items: start;
    min-height: 52px;
    padding: 7px;
    border: 1px solid #E2E8F0;
    border-radius: 8px;
    background: #FFFFFF;
  }

  .olreadia-job-drawer-fact > span {
    display: inline-flex;
    width: 24px;
    height: 24px;
    align-items: center;
    justify-content: center;
    border-radius: 7px;
    background: ${tokens.blueSoft};
    color: ${tokens.blue};
    font-size: 12px;
  }

  .olreadia-job-drawer-fact .ant-typography {
    font-size: 11px;
  }

  .olreadia-job-drawer-fact strong {
    display: block;
    color: ${tokens.text};
    font-size: 12px;
    line-height: 1.22;
  }

  .olreadia-job-drawer-block,
  .olreadia-job-drawer-section {
    display: grid;
    gap: 5px;
    padding: 8px;
    border: 1px solid #E2E8F0;
    border-radius: 8px;
    background: #FFFFFF;
  }

  .olreadia-job-drawer-block > .ant-typography,
  .olreadia-job-drawer-section > .ant-typography {
    color: ${tokens.darkGreen};
    font-size: 11px;
    font-weight: 900;
  }

  .olreadia-job-drawer-section .olreadia-rich-text {
    color: ${tokens.text};
    font-size: 12px;
    line-height: 1.45;
  }

  .olreadia-job-drawer-poster {
    border-color: #D6F0E1;
    background: #F8FFFA;
  }

  .olreadia-job-drawer-poster strong {
    color: ${tokens.text};
    font-size: 13px;
    line-height: 1.25;
  }

  .olreadia-job-drawer-poster span {
    color: ${tokens.muted};
    font-size: 12px;
    line-height: 1.35;
  }

  .olreadia-result-title {
    display: block;
    color: ${tokens.text};
    font-size: 16px;
    line-height: 1.25;
    font-weight: 900;
  }

  .olreadia-result-subtitle {
    display: block;
    margin-top: 3px;
    color: ${tokens.green};
    font-size: 13px;
    font-weight: 900;
  }

  .olreadia-result-summary {
    display: block;
    margin-top: 6px;
    color: ${tokens.muted};
    font-size: 13px;
    line-height: 1.45;
    font-weight: 400;
  }

  .olreadia-result-skills {
    display: flex;
    gap: 6px;
    flex-wrap: wrap;
    margin-top: 8px;
  }

  .olreadia-result-skill {
    border-radius: 999px;
    padding: 4px 8px;
    border: 1px solid ${tokens.border};
    color: ${tokens.muted};
    background: #F8FAFC;
    font-size: 11px;
    font-weight: 800;
  }

  .olreadia-choice-stack {
    display: grid;
    gap: 10px;
    position: sticky;
    top: 14px;
  }

  .olreadia-main-grid.is-home .olreadia-choice-stack {
    position: static;
    grid-template-columns: 1fr;
    align-items: stretch;
    gap: 8px;
  }

  .olreadia-main-grid.is-home .olreadia-side-cta-row,
  .olreadia-main-grid.is-home .olreadia-background-check-promo {
    grid-column: 1 / -1;
  }

  .olreadia-mobile-choice-panel {
    display: grid;
    gap: 8px;
    height: auto;
    min-height: 0;
    align-content: start;
    align-items: start;
    outline: none;
  }

  .olreadia-main-grid.is-home .olreadia-mobile-choice-panel {
    grid-template-columns: repeat(3, minmax(0, 1fr));
    grid-auto-rows: max-content;
    gap: 8px;
    margin-top: 0;
  }

  .olreadia-main-grid.is-home .olreadia-mobile-choice-panel .olreadia-mini-features {
    grid-column: 1 / -1;
  }

  .olreadia-nearby-section {
    display: grid;
    gap: 8px;
    margin-bottom: 0;
  }

  .olreadia-main-grid.is-home .olreadia-nearby-section {
    margin-bottom: 0;
    padding: 8px;
    border: 1px solid rgba(183, 228, 199, 0.9);
    border-radius: 16px;
    background:
      linear-gradient(135deg, rgba(255,255,255,0.96) 0%, rgba(242,252,248,0.96) 54%, rgba(244,251,255,0.95) 100%);
    box-shadow: 0 20px 48px rgba(15,23,42,0.09), inset 0 1px 0 rgba(255,255,255,0.9);
  }

  .olreadia-request-search-heading {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    gap: 14px;
    flex-wrap: wrap;
  }

  .olreadia-request-search-heading h2 {
    margin: 0;
    color: ${tokens.darkGreen};
    font-size: 24px;
    line-height: 1.16;
    font-weight: 950;
  }

  .olreadia-request-search-heading p {
    margin: 5px 0 0;
    max-width: 760px;
    color: ${tokens.muted};
    font-size: 14px;
    line-height: 1.45;
    font-weight: 650;
  }

  .olreadia-request-search-badge {
    display: inline-flex;
    min-height: 30px;
    align-items: center;
    gap: 7px;
    padding: 6px 11px;
    border: 1px solid #D6F0E1;
    border-radius: 999px;
    background: #FFFFFF;
    color: ${tokens.green};
    font-size: 11px;
    font-weight: 950;
    white-space: nowrap;
  }

  .olreadia-location-box {
    position: relative;
    overflow: hidden;
    padding: 12px;
    border: 1px solid rgba(39, 174, 48, 0.34);
    border-radius: 20px;
    background:
      linear-gradient(135deg, rgba(255,255,255,0.96), rgba(231,246,239,0.92) 54%, rgba(234,244,255,0.88)),
      #F2FCF8;
    box-shadow: inset 0 1px 0 rgba(255,255,255,0.95), 0 14px 30px rgba(39,174,48,0.10);
  }

  .olreadia-location-box::before {
    content: "";
    position: absolute;
    inset: 0;
    pointer-events: none;
    background: linear-gradient(110deg, transparent 0%, rgba(255,255,255,0.62) 42%, transparent 70%);
    transform: translateX(-64%);
    opacity: 0.7;
  }

  .olreadia-main-grid.is-home .olreadia-location-box {
    padding: 12px;
    background:
      linear-gradient(135deg, rgba(255,255,255,0.98), rgba(247,255,249,0.98) 58%, rgba(245,251,255,0.96)),
      #FFFFFF;
    box-shadow: inset 0 1px 0 rgba(255,255,255,1), 0 16px 34px rgba(15,23,42,0.08);
  }

  .olreadia-location-row {
    position: relative;
    z-index: 1;
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    gap: 10px;
    align-items: center;
  }

  .olreadia-location-row .ant-input-affix-wrapper,
  .olreadia-location-row .ant-btn {
    min-height: 50px;
    border-radius: 999px;
    font-weight: 900;
    font-size: 16px;
  }

  .olreadia-location-row .ant-input-affix-wrapper {
    border-color: #A7DDB8;
  }

  .olreadia-location-row .ant-input-prefix {
    color: ${tokens.darkGreen};
  }

  .olreadia-location-input-wrap {
    position: relative;
    min-width: 0;
  }

  .olreadia-location-input-wrap .ant-select {
    width: 100%;
  }

  .olreadia-location-input-wrap .ant-select-selector {
    min-height: 50px;
    border-color: rgba(39, 174, 48, 0.34) !important;
    border-radius: 999px !important;
    background:
      linear-gradient(180deg, #FFFFFF 0%, #FBFFFC 100%) !important;
    box-shadow: inset 0 1px 0 rgba(255,255,255,1), 0 8px 18px rgba(15,23,42,0.06) !important;
    font-weight: 900;
    align-items: center;
    transition: border-color 160ms ease, box-shadow 160ms ease, transform 160ms ease;
  }

  .olreadia-location-input-wrap .ant-select:hover .ant-select-selector,
  .olreadia-location-input-wrap .ant-select-focused .ant-select-selector {
    border-color: ${tokens.green} !important;
    box-shadow:
      inset 0 1px 0 rgba(255,255,255,1),
      0 0 0 4px rgba(39, 174, 48, 0.13),
      0 14px 28px rgba(39,174,48,0.12) !important;
  }

  .olreadia-location-input-wrap .ant-select:hover .ant-select-selector {
    transform: translateY(-1px);
  }

  .olreadia-location-input-wrap .ant-select-selection-search-input {
    min-height: 48px;
    color: ${tokens.darkGreen};
    font-size: 17px !important;
    font-weight: 900;
  }

  .olreadia-location-input-wrap .ant-select-selection-placeholder,
  .olreadia-location-input-wrap .ant-select-selection-item {
    display: flex;
    align-items: center;
    min-height: 48px;
    color: ${tokens.darkGreen};
    font-size: 17px;
    font-weight: 900;
  }

  .olreadia-location-input-wrap .ant-select-selection-placeholder {
    color: rgba(14, 86, 4, 0.58);
  }

  .olreadia-location-row > .ant-btn {
    border: 0;
    padding-inline: 18px;
    background: linear-gradient(135deg, ${tokens.green}, ${tokens.darkGreen});
    color: #FFFFFF;
    box-shadow: 0 12px 24px rgba(39,174,48,0.24);
  }

  .olreadia-location-row > .ant-btn:hover,
  .olreadia-location-row > .ant-btn:focus {
    background: linear-gradient(135deg, ${tokens.darkGreen}, ${tokens.green});
    color: #FFFFFF !important;
    box-shadow: 0 16px 28px rgba(39,174,48,0.28);
    transform: translateY(-1px);
  }

  .olreadia-address-suggestions {
    position: absolute;
    top: calc(100% + 6px);
    left: 0;
    right: 0;
    z-index: 20;
    overflow: hidden;
    border: 1px solid #D6F0E1;
    border-radius: 14px;
    background: #FFFFFF;
    box-shadow: 0 14px 30px rgba(15, 23, 42, 0.14);
  }

  .olreadia-address-suggestions button {
    width: 100%;
    min-height: 42px;
    border: 0;
    border-bottom: 1px solid #EEF2EE;
    padding: 10px 12px;
    background: #FFFFFF;
    color: ${tokens.text};
    cursor: pointer;
    font-size: 12.5px;
    font-weight: 800;
    text-align: left;
  }

  .olreadia-address-suggestions button:first-child {
    color: ${tokens.darkGreen};
    background: #F2FCF8;
    font-weight: 900;
  }

  .olreadia-address-suggestions button:last-child {
    border-bottom: 0;
  }

  .olreadia-address-suggestions button:hover {
    background: #F6FFF6;
  }

  .olreadia-address-suggestion-state {
    display: block;
    padding: 10px 12px;
    color: ${tokens.muted};
    background: #F8FAFC;
    font-size: 12px;
    font-weight: 750;
    line-height: 1.35;
  }

  .olreadia-location-active {
    display: block;
    margin-top: 7px;
    color: ${tokens.darkGreen};
    font-size: 11px;
    font-weight: 800;
  }

  .olreadia-nearby-toggle,
  .olreadia-nearby-empty {
    width: 100%;
    border: 1px solid #E2E8F0;
    border-radius: 18px;
    background: #F8FAFC;
    padding: 14px;
  }

  .olreadia-nearby-toggle {
    display: flex;
    align-items: center;
    justify-content: space-between;
    cursor: pointer;
    text-align: left;
  }

  .olreadia-nearby-toggle strong,
  .olreadia-nearby-empty strong {
    display: block;
    color: ${tokens.green};
    font-size: 14px;
    font-weight: 900;
  }

  .olreadia-nearby-toggle small,
  .olreadia-nearby-empty .ant-typography {
    display: block;
    margin-top: 4px;
    color: ${tokens.darkRed};
    font-size: 11px;
    line-height: 1.45;
  }

  .olreadia-nearby-toggle b {
    color: #94A3B8;
    font-size: 22px;
  }

  .olreadia-nearby-view-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    flex-wrap: wrap;
  }

  .olreadia-nearby-view-toggle {
    display: inline-grid;
    grid-template-columns: repeat(2, minmax(104px, 1fr));
    gap: 5px;
    padding: 5px;
    border: 1px solid #D6F0E1;
    border-radius: 999px;
    background: #FFFFFF;
  }

  .olreadia-nearby-view-toggle button {
    min-height: 32px;
    border: 0;
    border-radius: 999px;
    background: transparent;
    color: ${tokens.darkGreen};
    cursor: pointer;
    font-size: 12px;
    font-weight: 900;
  }

  .olreadia-nearby-view-toggle button.active {
    background: ${tokens.green};
    color: #FFFFFF;
  }

  .olreadia-nearby-view-bar strong {
    display: block;
    color: ${tokens.green};
    font-size: 14px;
    font-weight: 900;
  }

  .olreadia-nearby-view-bar small {
    display: block;
    margin-top: 4px;
    color: ${tokens.darkRed};
    font-size: 11px;
    line-height: 1.45;
  }

  .olreadia-nearby-map {
    position: relative;
    width: 100%;
    min-height: 560px;
    margin: 0;
    border: 1px solid #D6F0E1;
    border-radius: 14px;
    background: #EAF7EF;
    overflow: hidden;
  }

  .olreadia-nearby-map-canvas {
    width: 100%;
    height: 560px;
  }

  .olreadia-nearby-map-empty {
    position: absolute;
    inset: 14px;
    display: grid;
    place-items: center;
    border-radius: 14px;
    background: rgba(255,255,255,0.86);
    color: ${tokens.darkGreen};
    font-size: 13px;
    font-weight: 900;
    text-align: center;
    pointer-events: none;
  }

  .olreadia-map-task-summary-modal .ant-modal-content {
    border-radius: 18px;
    border: 1px solid rgba(35,133,219,0.22);
    padding: 0;
    overflow: hidden;
    box-shadow: 0 18px 38px rgba(15,23,42,0.16);
  }

  .olreadia-map-task-summary-modal .ant-modal-close {
    top: 8px;
    right: 8px;
  }

  .olreadia-map-task-summary {
    padding: 14px;
    background: #FFFFFF;
  }

  .olreadia-map-task-summary__top {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    padding-right: 24px;
  }

  .olreadia-map-task-summary__top h3 {
    flex: 1;
    min-width: 0;
    margin: 0;
    overflow: hidden;
    color: ${tokens.green};
    font-size: 14px;
    font-weight: 900;
    line-height: 1.25;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .olreadia-map-task-summary__status {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    max-width: 112px;
    padding: 4px 8px;
    border: 1px solid ${tokens.green};
    border-radius: 999px;
    color: ${tokens.green};
    font-size: 10px;
    font-weight: 900;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .olreadia-map-task-summary__status i {
    width: 7px;
    height: 7px;
    border-radius: 999px;
    background: ${tokens.green};
    flex: 0 0 auto;
  }

  .olreadia-map-task-summary p {
    margin: 7px 0 0;
    overflow: hidden;
    color: ${tokens.muted};
    font-size: 12px;
    font-weight: 800;
    line-height: 1.45;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .olreadia-map-task-summary__meta {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 8px;
    margin-top: 11px;
  }

  .olreadia-map-task-summary__meta span {
    min-width: 0;
    padding: 8px 10px;
    border: 1px solid rgba(22,163,74,0.18);
    border-radius: 14px;
    background: rgba(22,163,74,0.10);
  }

  .olreadia-map-task-summary__meta span:nth-child(2) {
    border-color: rgba(35,133,219,0.18);
    background: rgba(35,133,219,0.10);
  }

  .olreadia-map-task-summary__meta small {
    display: block;
    color: ${tokens.muted};
    font-size: 9px;
    font-weight: 900;
    letter-spacing: 0.6px;
  }

  .olreadia-map-task-summary__meta strong {
    display: block;
    margin-top: 2px;
    overflow: hidden;
    color: ${tokens.darkGreen};
    font-size: 12px;
    font-weight: 900;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .olreadia-map-task-summary__meta span:nth-child(2) strong {
    color: ${tokens.darkBlue};
  }

  .olreadia-map-task-summary__button,
  .olreadia-map-task-summary__button:hover,
  .olreadia-map-task-summary__button:focus {
    min-height: 40px;
    margin-top: 11px;
    border-color: ${tokens.green};
    border-radius: 14px;
    background: ${tokens.green};
    color: #FFFFFF;
    font-size: 12px;
    font-weight: 900;
    letter-spacing: 0.4px;
  }

  .olreadia-radius-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
  }

  .olreadia-radius-row .ant-typography {
    color: ${tokens.darkBlue};
    font-size: 12px;
    font-weight: 900;
  }

  .olreadia-radius-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }

  .olreadia-radius-chips button {
    border: 1px solid #B7D8F5;
    border-radius: 999px;
    padding: 7px 12px;
    background: #F3F8FF;
    color: ${tokens.blue};
    cursor: pointer;
    font-size: 11px;
    font-weight: 900;
  }

  .olreadia-radius-chips button.active {
    border-color: ${tokens.blue};
    background: ${tokens.blue};
    color: #FFFFFF;
  }

  .olreadia-nearby-list {
    display: grid;
    gap: 8px;
  }

  .olreadia-main-grid.is-home .olreadia-nearby-list {
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 12px;
  }

  .olreadia-preview-card {
    width: 100%;
    border: 1px solid #E5E7EB;
    border-radius: 16px;
    display: grid;
    grid-template-columns: 68px minmax(0, 1fr) 18px;
    gap: 8px;
    padding: 10px;
    background: #FFFFFF;
    cursor: pointer;
    text-align: left;
  }

  .olreadia-main-grid.is-home .olreadia-preview-card {
    min-height: 120px;
    grid-template-columns: 82px minmax(0, 1fr) 18px;
    padding: 12px;
  }

  .olreadia-preview-card img {
    width: 68px;
    height: 68px;
    border-radius: 10px;
    background: ${tokens.blueSoft};
    object-fit: cover;
  }

  .olreadia-main-grid.is-home .olreadia-preview-card img {
    width: 82px;
    height: 82px;
  }

  .olreadia-preview-card span {
    min-width: 0;
    display: grid;
    gap: 2px;
  }

  .olreadia-preview-card strong {
    color: ${tokens.darkGreen};
    font-size: 12px;
    font-weight: 900;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .olreadia-preview-card small,
  .olreadia-preview-card em {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .olreadia-preview-card small {
    color: ${tokens.muted};
    font-size: 11px;
  }

  .olreadia-preview-card em {
    color: ${tokens.blue};
    font-size: 10px;
    font-style: normal;
    font-weight: 800;
  }

  .olreadia-preview-card b {
    align-self: center;
    color: #94A3B8;
    font-size: 22px;
  }

  .olreadia-view-more {
    justify-self: center;
    border-color: #D6F0E1;
    border-radius: 8px;
    background: ${tokens.greenSoft};
    color: ${tokens.green};
    font-weight: 900;
  }

  .olreadia-ads-section {
    display: grid;
    gap: 8px;
    padding: 10px;
    border: 1px solid #D7EAFE;
    border-radius: 12px;
    background: #FFFFFF;
    box-shadow: 0 8px 18px rgba(15,23,42,0.05);
  }

  .olreadia-inline-ads-section.olreadia-ads-section {
    position: relative;
    box-sizing: border-box;
    width: 100%;
    min-width: 0;
    max-width: 100%;
    justify-self: stretch;
    overflow: hidden;
    padding: 8px;
    border-radius: 10px;
    box-shadow: none;
  }

  .olreadia-inline-ads-section *,
  .olreadia-inline-ads-section *::before,
  .olreadia-inline-ads-section *::after {
    box-sizing: border-box;
  }

  .olreadia-inline-ads-section .oliveeb-product-ads__header {
    display: flex;
  }

  .olreadia-inline-ads-section .oliveeb-product-ad {
    grid-template-columns: 58px minmax(0, 1fr);
    width: 100%;
    min-height: 72px;
    min-width: 0;
    max-width: 100%;
    overflow: hidden;
    padding: 7px;
  }

  .olreadia-inline-ads-section .oliveeb-product-ad__copy {
    width: 100%;
    min-width: 0;
    max-width: 100%;
    overflow: hidden;
  }

  .olreadia-inline-ads-section .oliveeb-product-ad__icon {
    width: 58px;
    height: 58px;
    border-radius: 10px;
  }

  .olreadia-inline-ads-section .oliveeb-product-ad__icon img {
    height: 48px;
    max-height: 48px;
  }

  .olreadia-inline-ads-section .oliveeb-product-ad__title {
    font-size: 13px;
    max-width: 100%;
    white-space: normal;
    overflow-wrap: anywhere;
    word-break: break-word;
  }

  .olreadia-inline-ads-section .oliveeb-product-ad__text {
    font-size: 11px;
    line-height: 1.25;
    max-width: 100%;
    white-space: normal;
    overflow-wrap: anywhere;
    word-break: break-word;
  }

  .olreadia-inline-ads-section .oliveeb-product-ad__link {
    max-width: 100%;
    white-space: normal;
    overflow-wrap: anywhere;
    word-break: break-word;
  }

  .olreadia-about-section {
    margin-top: 8px;
    padding: 16px;
    border: 1px solid ${tokens.border};
    border-radius: 12px;
    background:
      linear-gradient(135deg, rgba(242,248,255,0.88) 0%, rgba(255,255,255,0.98) 46%, rgba(242,255,247,0.9) 100%);
    box-shadow: 0 14px 32px rgba(15,23,42,0.07);
  }

  .olreadia-about-layout {
    display: grid;
    grid-template-columns: minmax(0, 1.05fr) minmax(0, 0.95fr);
    gap: 14px;
    align-items: center;
  }

  .olreadia-about-layout > div {
    min-width: 0;
  }

  .olreadia-about-kicker {
    display: inline-flex;
    align-items: center;
    width: fit-content;
    min-height: 24px;
    margin-bottom: 10px;
    border-radius: 999px;
    padding: 4px 10px;
    border: 1px solid #D7EAFE;
    color: ${tokens.blue};
    background: ${tokens.blueSoft};
    font-size: 10px;
    font-weight: 950;
    text-transform: uppercase;
  }

  .olreadia-about-title {
    margin: 0;
    color: ${tokens.green};
    font-size: 28px;
    line-height: 1.14;
    font-weight: 950;
  }

  .olreadia-about-copy {
    margin: 10px 0 0;
    color: ${tokens.muted};
    font-size: 24px;
    line-height: 1.6;
    font-weight: 500;
    overflow-wrap: anywhere;
  }

  .olreadia-about-actions {
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
    margin-top: 12px;
  }

  .olreadia-about-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px;
  }

  .olreadia-about-media {
    margin: 0 0 12px;
    aspect-ratio: 1829 / 860;
    border-radius: 12px;
    border: 1px solid #D7EAFE;
    background: #FFFFFF;
    box-shadow: 0 14px 30px rgba(15,23,42,0.08);
    overflow: hidden;
  }

  .olreadia-about-media img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .olreadia-about-item {
    min-height: 132px;
    padding: 14px;
    border-radius: 10px;
    border: 1px solid #E2E8F0;
    background: #FFFFFF;
    box-shadow: 0 10px 22px rgba(15,23,42,0.05);
  }

  .olreadia-about-icon {
    display: inline-flex;
    width: 38px;
    height: 38px;
    align-items: center;
    justify-content: center;
    border-radius: 999px;
    margin-bottom: 10px;
    color: ${tokens.green};
    background: ${tokens.greenSoft};
    border: 1px solid #D6F0E1;
    font-size: 18px;
  }

  .olreadia-about-item.is-blue .olreadia-about-icon {
    color: ${tokens.blue};
    background: ${tokens.blueSoft};
    border-color: #D7EAFE;
  }

  .olreadia-about-item.is-gold .olreadia-about-icon {
    color: ${tokens.darkRed};
    background: ${tokens.goldSoft};
    border-color: ${tokens.goldBorder};
  }

  .olreadia-about-item-title {
    display: block;
    color: ${tokens.text};
    font-size: 14px;
    line-height: 1.25;
    font-weight: 950;
  }

  .olreadia-about-item-text {
    display: block;
    margin-top: 6px;
    color: ${tokens.muted};
    font-size: 12px;
    line-height: 1.45;
    font-weight: 600;
  }

  .olreadia-ads-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
  }

  .olreadia-ads-title {
    color: ${tokens.text};
    font-size: 13px;
    line-height: 1.2;
    font-weight: 950;
  }

  .olreadia-ads-label {
    border-radius: 999px;
    padding: 3px 7px;
    border: 1px solid #D7EAFE;
    background: ${tokens.blueSoft};
    color: ${tokens.blue};
    font-size: 9px;
    line-height: 1.1;
    font-weight: 900;
    text-transform: uppercase;
  }

  .olreadia-ad-item {
    display: grid;
    grid-template-columns: 86px minmax(0, 1fr);
    gap: 12px;
    align-items: center;
    min-height: 108px;
    padding: 10px;
    border: 1px solid #E2E8F0;
    border-radius: 8px;
    background: #FFFFFF;
    transition: border-color 180ms ease, background 180ms ease;
  }

  .olreadia-ad-icon {
    display: inline-flex;
    width: 86px;
    height: 86px;
    align-items: center;
    justify-content: center;
    border-radius: 12px;
    background: #FFFFFF;
    border: 1px solid #E2E8F0;
    overflow: hidden;
  }

  .olreadia-ad-icon img {
    display: block;
    width: auto;
    max-width: 100%;
    height: 70px;
    max-height: 70px;
    object-fit: contain;
    padding: 2px;
  }

  .olreadia-ad-item.blue {
    border-color: #D7EAFE;
  }

  .olreadia-ad-item.gold {
    border-color: ${tokens.goldBorder};
  }

  .olreadia-ad-title {
    display: block;
    color: ${tokens.green};
    font-size: 17px;
    line-height: 1.2;
    font-weight: 900;
  }

  .olreadia-ad-text {
    display: block;
    margin-top: 4px;
    color: ${tokens.muted};
    font-size: 13px;
    line-height: 1.34;
  }

  .olreadia-ad-link {
    color: ${tokens.blue};
    font-weight: 950;
    text-decoration: none;
    white-space: nowrap;
  }

  .olreadia-ad-link:hover {
    text-decoration: underline;
  }

  .olreadia-ad-dots {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 5px;
  }

  .olreadia-ad-dot {
    width: 7px;
    height: 7px;
    padding: 0;
    border: 0;
    border-radius: 999px;
    background: #CBD5E1;
    cursor: pointer;
  }

  .olreadia-ad-dot.is-active {
    width: 18px;
    background: ${tokens.blue};
  }

  .olreadia-choice-card {
    position: relative;
    overflow: hidden;
    display: grid;
    grid-template-columns: 48px minmax(0, 1fr) 18px;
    gap: 10px;
    align-items: center;
    min-height: 74px;
    width: 100%;
    margin: 0;
    padding: 10px 12px;
    border: 1px solid #D6F0E1;
    border-radius: 16px;
    background: #F7FFF9;
    color: inherit;
    text-align: left;
    cursor: pointer;
    box-shadow: none;
  }

  .olreadia-choice-card.blue {
    border-color: #D7EAFE;
    background: #F8FBFF;
  }

  .olreadia-choice-card.gold {
    border-color: ${tokens.goldBorder};
    background: ${tokens.goldSoft};
  }

  .olreadia-choice-glow {
    position: absolute;
    top: -18px;
    right: -10px;
    width: 82px;
    height: 82px;
    border-radius: 999px;
    opacity: 0.45;
    pointer-events: none;
  }

  .olreadia-choice-glow-green {
    background: #DDF7E8;
  }

  .olreadia-choice-glow-blue {
    background: #DCEEFF;
  }

  .olreadia-choice-icon {
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 48px;
    height: 48px;
    border-radius: 16px;
    color: ${tokens.green};
    background: ${tokens.greenSoft};
    font-size: 22px;
  }

  .olreadia-choice-card.blue .olreadia-choice-icon {
    color: ${tokens.blue};
    background: ${tokens.blueSoft};
  }

  .olreadia-choice-card.gold .olreadia-choice-icon {
    color: ${tokens.darkRed};
    background: #FFFFFF;
  }

  .olreadia-choice-title {
    position: relative;
    display: block;
    color: ${tokens.green};
    font-size: 15px;
    line-height: 1.2;
    font-weight: 900;
  }

  .olreadia-choice-title strong {
    color: ${tokens.green};
  }

  .olreadia-choice-card.blue .olreadia-choice-title strong {
    color: ${tokens.blue};
  }

  .olreadia-choice-card.gold .olreadia-choice-title,
  .olreadia-choice-card.gold .olreadia-choice-title strong {
    color: ${tokens.darkRed};
  }

  .olreadia-choice-desc {
    position: relative;
    display: block;
    margin-top: 3px;
    color: ${tokens.muted};
    font-size: 11.6px;
    line-height: 1.38;
    font-weight: 500;
  }

  .olreadia-choice-arrow {
    position: relative;
    color: #94A3B8;
    font-size: 24px;
    font-weight: 900;
  }

  .olreadia-background-check-promo {
    display: grid;
    grid-template-columns: 38px minmax(0, 1fr) auto;
    gap: 10px;
    align-items: center;
    min-height: 76px;
    padding: 11px;
    border: 1px solid ${tokens.goldBorder};
    border-radius: 12px;
    background: #FFFFFF;
    box-shadow: 0 8px 18px rgba(15,23,42,0.05);
  }

  .olreadia-background-check-icon {
    display: inline-flex;
    width: 38px;
    height: 38px;
    align-items: center;
    justify-content: center;
    border-radius: 10px;
    background: #FFFFFF;
    color: ${tokens.darkRed};
    font-size: 18px;
  }

  .olreadia-background-check-title {
    display: block;
    color: ${tokens.text};
    font-size: 13px;
    line-height: 1.2;
    font-weight: 950;
  }

  .olreadia-background-check-text {
    display: block;
    margin-top: 3px;
    color: ${tokens.muted};
    font-size: 11px;
    line-height: 1.32;
  }

  .olreadia-background-check-action {
    min-width: 72px;
    height: 30px;
    border-radius: 8px;
    border-color: ${tokens.goldBorder};
    color: ${tokens.darkRed};
    background: #FFFFFF;
    font-size: 11px;
    font-weight: 900;
  }

  .olreadia-mini-features {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 8px;
    margin-top: 2px;
  }

  .olreadia-mini-features.is-bottom {
    margin-top: 8px;
    padding: 12px;
    border: 1px solid ${tokens.border};
    border-radius: 12px;
    background: #FFFFFF;
    box-shadow: 0 8px 18px rgba(15,23,42,0.05);
  }

  .olreadia-mini-feature {
    min-height: 42px;
    border-radius: 16px;
    border: 1px solid #D6F0E1;
    background: #F3FFF8;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    gap: 5px;
    color: ${tokens.darkGreen};
    font-size: 11px;
    font-weight: 900;
  }

  .olreadia-mini-feature.blue {
    border-color: #D7EAFE;
    background: ${tokens.blueSoft};
    color: ${tokens.blue};
  }

  .olreadia-mini-feature.gold {
    border-color: ${tokens.goldBorder};
    background: ${tokens.goldSoft};
    color: ${tokens.darkRed};
  }

  .olreadia-bottom-card {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 14px;
    flex-wrap: wrap;
    padding: 16px;
    margin-top: 8px;
    border-radius: 12px;
    border: 1px solid ${tokens.border};
    background: #FFFFFF;
    box-shadow: 0 8px 18px rgba(15,23,42,0.05);
  }

  .olreadia-public-page {
    margin-top: 16px;
  }

  .olreadia-public-hero {
    padding: 18px;
    border-radius: 12px;
    border: 1px solid ${tokens.border};
    background: #FFFFFF;
    box-shadow: 0 8px 18px rgba(15,23,42,0.05);
  }

  .olreadia-public-kicker {
    display: inline-flex;
    min-height: 24px;
    align-items: center;
    border-radius: 999px;
    padding: 4px 10px;
    border: 1px solid #D7EAFE;
    background: ${tokens.blueSoft};
    color: ${tokens.blue};
    font-size: 11px;
    font-weight: 900;
    text-transform: uppercase;
  }

  .olreadia-public-title {
    margin: 10px 0 0;
    color: ${tokens.darkGreen};
    font-size: 28px;
    line-height: 1.12;
    font-weight: 900;
  }

  .olreadia-public-deck {
    max-width: 920px;
    margin: 10px 0 0;
    color: ${tokens.darkGreen};
    font-size: 15px;
    line-height: 1.55;
    font-weight: 400;
  }

  .olreadia-public-body {
    max-width: 980px;
    margin: 8px 0 0;
    color: ${tokens.muted};
    font-size: 14px;
    line-height: 1.6;
    font-weight: 400;
  }

  .olreadia-public-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 12px;
    margin-top: 14px;
  }

  .olreadia-public-card {
    min-height: 112px;
    padding: 14px;
    border-radius: 10px;
    border: 1px solid ${tokens.border};
    background: #FFFFFF;
  }

  .olreadia-public-card:nth-child(2) {
    border-color: ${tokens.border};
    background: #FFFFFF;
  }

  .olreadia-public-card:nth-child(3) {
    border-color: ${tokens.border};
    background: #FFFFFF;
  }

  .olreadia-public-card-title {
    display: block;
    color: ${tokens.text};
    font-size: 15px;
    line-height: 1.3;
    font-weight: 900;
  }

  .olreadia-public-card-text {
    display: block;
    margin-top: 6px;
    color: ${tokens.muted};
    font-size: 13px;
    line-height: 1.5;
    font-weight: 400;
  }

  .olreadia-bottom-title {
    margin: 0;
    color: ${tokens.text};
    font-size: 20px;
    font-weight: 900;
  }

  .olreadia-footer {
    margin-top: 18px;
    padding-top: 14px;
    border-top: 1px solid ${tokens.border};
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    flex-wrap: wrap;
    color: ${tokens.muted};
    font-size: 12px;
  }

  .olreadia-modal-badge {
    display: inline-flex;
    align-items: center;
    min-height: 26px;
    padding: 4px 10px;
    border-radius: 999px;
    border: 1px solid #D6F0E1;
    background: ${tokens.greenSoft};
    color: ${tokens.darkGreen};
    font-size: 11px;
    font-weight: 900;
    text-transform: uppercase;
  }

  .olreadia-modal-title {
    margin: 14px 0 4px;
    color: ${tokens.text};
    font-size: 20px;
    line-height: 1.2;
    font-weight: 900;
  }

  .olreadia-modal-desc {
    margin: 0 0 16px;
    color: ${tokens.muted};
    font-size: 14px;
    line-height: 1.6;
    font-weight: 400;
  }

  .olreadia-share-qr {
    display: grid;
    justify-items: center;
    gap: 10px;
    margin: 4px 0 14px;
    padding: 16px;
    border: 1px solid #D6F0E1;
    border-radius: 16px;
    background: #F7FFF9;
  }

  .olreadia-share-qr-label {
    color: ${tokens.darkGreen};
    font-size: 12px;
    font-weight: 900;
  }

  .olreadia-share-link {
    padding: 12px;
    border-radius: 12px;
    border: 1px solid #D6F0E1;
    background: ${tokens.greenSoft};
    color: ${tokens.darkGreen};
    font-weight: 800;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .olreadia-modal-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    margin-top: 14px;
  }

  .olreadia-modal-actions .ant-btn-primary {
    background: ${tokens.green};
    border-color: ${tokens.green};
  }

  .olreadia-modal-actions .ant-btn:not(.ant-btn-primary) {
    color: ${tokens.darkGreen};
    border-color: #D6F0E1;
  }

  .olreadia-guest-task {
    display: grid;
    gap: 10px;
    background: #FFFFFF;
  }

  .olreadia-guest-task-topbar {
    display: grid;
    grid-template-columns: 36px 1fr 36px;
    align-items: center;
    min-height: 44px;
    border-bottom: 1px solid #E2E8F0;
    margin: -8px -8px 2px;
    padding: 0 4px 8px;
  }

  .olreadia-guest-task-topbar strong {
    color: ${tokens.green};
    font-size: 18px;
    font-weight: 900;
    text-align: center;
  }

  .olreadia-guest-task-progress {
    position: relative;
    margin: 4px 0 2px;
    padding: 12px 8px 4px;
  }

  .olreadia-guest-task-progress-lines {
    position: absolute;
    top: 25px;
    left: 34px;
    right: 34px;
    display: grid;
    grid-auto-flow: column;
    grid-auto-columns: 1fr;
    gap: 12px;
  }

  .olreadia-guest-task-progress-lines span {
    height: 2px;
    background: #DCDCDC;
  }

  .olreadia-guest-task-progress-lines span.is-active {
    background: #2ECC71;
  }

  .olreadia-guest-task-progress-steps {
    position: relative;
    z-index: 1;
    display: grid;
    grid-template-columns: repeat(6, minmax(0, 1fr));
    gap: 4px;
  }

  .olreadia-guest-task-progress-step {
    display: grid;
    justify-items: center;
    gap: 6px;
    min-width: 0;
  }

  .olreadia-guest-task-progress-step i {
    display: grid;
    place-items: center;
    width: 28px;
    height: 28px;
    border-radius: 50%;
    border: 2px solid #CCCCCC;
    background: #FFFFFF;
    color: #FFFFFF;
    font-style: normal;
    font-size: 13px;
    font-weight: 900;
  }

  .olreadia-guest-task-progress-step.is-complete i {
    border-color: #2ECC71;
    background: #2ECC71;
  }

  .olreadia-guest-task-progress-step.is-current i {
    border-color: #F1C40F;
    background: #F1C40F;
  }

  .olreadia-guest-task-progress-step.is-terminal i {
    border-color: ${tokens.red};
    background: ${tokens.red};
  }

  .olreadia-guest-task-progress-step small {
    color: #38383B;
    font-size: 10px;
    line-height: 12px;
    text-align: center;
    font-weight: 650;
  }

  .my-requests-status-progress {
    border: 1px solid #e6efe7;
    border-radius: 16px;
    background: #ffffff;
    margin: 12px 0;
    overflow: hidden;
    padding: 16px 16px 12px;
    position: relative;
  }

  .my-requests-status-progress__line {
    display: grid;
    grid-template-columns: repeat(var(--task-line-count), minmax(0, 1fr));
    left: calc(50% / var(--task-stage-count));
    position: absolute;
    top: 29px;
    width: calc(100% - (100% / var(--task-stage-count)));
    z-index: 0;
  }

  .my-requests-status-progress__line span {
    background: #dcdcdc;
    height: 2px;
    margin: 0;
  }

  .my-requests-status-progress__line span.is-active {
    background: #2ecc71;
  }

  .my-requests-status-progress__steps {
    align-items: flex-start;
    display: flex;
    justify-content: space-between;
    margin: 0 auto;
    position: relative;
    z-index: 1;
  }

  .my-requests-status-progress__step {
    align-items: center;
    display: grid;
    flex: 1;
    gap: 6px;
    justify-items: center;
    min-width: 0;
    position: relative;
    text-align: center;
  }

  .my-requests-status-progress__step::before,
  .my-requests-status-progress__step::after {
    display: none;
  }

  .my-requests-status-progress__circle {
    width: clamp(22px, 2.1vw, 28px);
    height: clamp(22px, 2.1vw, 28px);
    border: 2px solid #cccccc;
    border-radius: 999px;
    background: #ffffff;
    color: #ffffff;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    font-weight: 900;
    line-height: 1;
  }

  .my-requests-status-progress__circle.is-completed {
    border-color: #2ecc71;
    background: #2ecc71;
  }

  .my-requests-status-progress__circle.is-current {
    border-color: #f1c40f;
    background: #f1c40f;
  }

  .my-requests-status-progress__circle.is-cancelled {
    border-color: #e74c3c;
    background: #e74c3c;
  }

  .my-requests-status-progress__circle.is-expired {
    border-color: #9b9b9b;
    background: #9b9b9b;
  }

  .my-requests-status-progress__step strong {
    color: #062701;
    font-size: clamp(8px, 1.1vw, 10px);
    font-weight: 800;
    line-height: 1.15;
    max-width: min(72px, 100%);
    overflow-wrap: anywhere;
  }

  .olreadia-guest-task-head {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 8px;
  }

  .olreadia-guest-task-head .ant-tag {
    border-radius: 999px;
    border-color: rgba(9, 3, 89, 0.2);
    background: rgba(9, 3, 89, 0.08);
    color: ${tokens.blue};
    font-size: 12px;
    font-weight: 900;
  }

  .olreadia-guest-task h2 {
    margin: 0;
    color: ${tokens.darkGreen};
    font-size: 21px;
    line-height: 1.22;
    font-weight: 900;
  }

  .olreadia-guest-task h3 {
    margin: 0;
    color: ${tokens.green};
    font-size: 16px;
    font-weight: 800;
  }

  .olreadia-guest-task-desc {
    margin: 8px 0 0;
    color: ${tokens.darkGreen};
    font-size: 15px;
    line-height: 1.45;
    font-weight: 500;
  }

  .olreadia-guest-task-summary {
    position: relative;
    overflow: hidden;
    display: grid;
    gap: 10px;
    margin-top: 4px;
    padding: 14px;
    border: 1px solid #BDECC9;
    border-radius: 16px;
    background: #F7FFFA;
    box-shadow: 0 8px 18px rgba(39, 174, 48, 0.14);
  }

  .olreadia-guest-task-glow {
    position: absolute;
    top: -30px;
    right: -20px;
    width: 130px;
    height: 130px;
    border-radius: 50%;
    background: rgba(39, 174, 48, 0.10);
  }

  .olreadia-guest-task-summary > *:not(.olreadia-guest-task-glow) {
    position: relative;
    z-index: 1;
  }

  .olreadia-guest-task-metrics {
    display: grid;
    gap: 10px;
  }

  .olreadia-guest-task-metrics > span {
    display: grid;
    grid-template-columns: 20px 1fr;
    align-items: center;
    gap: 8px;
    border: 1px solid #D8EDE0;
    border-radius: 12px;
    background: #FFFFFF;
    padding: 8px 10px;
  }

  .olreadia-guest-task-metrics > span > .anticon {
    color: ${tokens.green};
    font-size: 16px;
  }

  .olreadia-guest-task-card,
  .olreadia-guest-task-locked {
    border: 1px solid #DCEEE4;
    border-radius: 14px;
    background: #FFFFFF;
    padding: 14px;
  }

  .olreadia-guest-task-label {
    display: block;
    color: ${tokens.green};
    font-size: 12px;
    font-weight: 700;
  }

  .olreadia-guest-task-value {
    display: block;
    margin-top: 1px;
    color: ${tokens.text};
    font-size: 14px;
    font-weight: 800;
  }

  .olreadia-guest-task-value.is-offered {
    color: ${tokens.red};
    font-weight: 900;
  }

  .olreadia-guest-task-primary,
  .olreadia-guest-task-primary:hover,
  .olreadia-guest-task-primary:focus {
    min-height: 46px;
    border-radius: 12px;
    border-color: ${tokens.green};
    background: ${tokens.green};
    font-size: 15px;
    font-weight: 900;
    box-shadow: 0 8px 16px rgba(39, 174, 48, 0.22);
  }

  .olreadia-guest-task-photo {
    overflow: hidden;
    border-radius: 12px;
    border: 1px solid #E2E8F0;
    background: #E2E8F0;
    aspect-ratio: 16 / 10;
  }

  .olreadia-guest-task-photo img,
  .olreadia-guest-task-gallery img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .olreadia-guest-task-section-head {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-bottom: 8px;
    color: ${tokens.green};
  }

  .olreadia-guest-task-chips,
  .olreadia-guest-task-gallery {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }

  .olreadia-guest-task-chips span {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    border: 1px solid #BDE3C6;
    border-radius: 16px;
    background: #EAFAEA;
    padding: 6px 10px;
    color: #063A06;
    font-size: 13px;
    font-weight: 600;
  }

  .olreadia-guest-task-chips .anticon {
    color: ${tokens.green};
    font-size: 14px;
  }

  .olreadia-guest-task-gallery img {
    width: calc(33.333% - 6px);
    min-width: 120px;
    height: 92px;
    border-radius: 12px;
    border: 1px solid #E2E8F0;
    background: #E2E8F0;
  }

  .olreadia-guest-task-locked {
    display: grid;
    gap: 8px;
    background:
      linear-gradient(135deg, rgba(231,246,239,0.96), rgba(255,255,255,0.98)),
      #FFFFFF;
  }

  .olreadia-guest-task-locked h3,
  .olreadia-guest-task-locked p {
    margin: 0;
  }

  .olreadia-guest-task-locked h3 {
    color: ${tokens.green};
    font-size: 18px;
    font-weight: 950;
  }

  .olreadia-guest-task-locked p {
    color: ${tokens.muted};
    font-size: 13px;
    line-height: 1.5;
    font-weight: 650;
  }

  .olreadia-guest-task-actions {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px;
    margin-top: 4px;
  }

  .olreadia-guest-task-actions .ant-btn {
    border-radius: 14px;
    font-weight: 900;
  }

  .olreadia-guest-task-actions .ant-btn-primary {
    border-color: ${tokens.green};
    background: ${tokens.green};
  }

  @media (max-width: 860px) {
    .olreadia-landing-header {
      grid-template-columns: 1fr;
      align-items: stretch;
      text-align: left;
      gap: 14px;
      padding-bottom: 14px;
    }

    .olreadia-welcome-brand {
      width: min(100%, 520px);
    }

    .olreadia-header-content {
      align-items: stretch;
      justify-content: flex-start;
      height: auto;
      padding: 0;
    }

    .olreadia-header-actions {
      align-items: stretch;
    }

    .olreadia-public-nav {
      justify-content: flex-start;
    }

    .olreadia-mobile-nearby-button {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      min-height: 38px;
      width: 100%;
      border-radius: 999px;
      border-color: ${tokens.green};
      background: ${tokens.greenSoft};
      color: ${tokens.green};
      font-size: 13px;
      font-weight: 900;
      box-shadow: 0 8px 18px rgba(39,174,48,0.12);
    }

    .olreadia-mobile-nearby-button:hover,
    .olreadia-mobile-nearby-button:focus {
      border-color: ${tokens.green};
      background: ${tokens.green};
      color: #FFFFFF;
    }

    .olreadia-main-grid {
      grid-template-columns: 1fr;
      gap: 12px;
    }

    .olreadia-choice-stack {
      position: static;
    }

    .olreadia-main-grid.is-home .olreadia-choice-stack,
    .olreadia-main-grid.is-home .olreadia-nearby-list {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .olreadia-promo-row {
      width: 100%;
      grid-template-columns: minmax(0, 1fr) minmax(118px, 0.56fr);
      text-align: left;
      justify-items: stretch;
      gap: 10px;
      padding: 14px;
    }

    .olreadia-promo-row .olreadia-inline-ads-section,
    .olreadia-promo-row .pm-right-ads-section {
      grid-column: 1 / -1;
      width: 100%;
      max-width: 100%;
      justify-self: stretch;
    }

    .olreadia-promo-row .olreadia-inline-ads-section .oliveeb-product-ad {
      grid-template-columns: minmax(0, 1fr);
      width: 100%;
      max-width: 100%;
      justify-items: start;
    }

    .olreadia-promo-row .olreadia-inline-ads-section .oliveeb-product-ad__icon {
      width: 100%;
      height: 46px;
    }

    .olreadia-promo-row .olreadia-inline-ads-section .oliveeb-product-ad__icon img {
      height: 34px;
      max-height: 34px;
    }

    .olreadia-subhead-audience {
      font-size: 15px;
      text-align: left;
    }

    .olreadia-public-grid {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .olreadia-about-layout {
      grid-template-columns: 1fr;
      gap: 16px;
      align-items: stretch;
    }

    .olreadia-about-grid {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .olreadia-bottom-card {
      align-items: flex-start;
    }
  }

  @media (max-width: 560px) {
    .olreadia-location-row,
    .olreadia-guest-task-actions {
      grid-template-columns: 1fr;
    }

    .olreadia-guest-task-progress {
      padding-inline: 0;
    }

    .olreadia-guest-task-progress-lines {
      left: 26px;
      right: 26px;
      gap: 7px;
    }

    .my-requests-status-progress {
      padding-left: 8px;
      padding-right: 8px;
    }

    .my-requests-status-progress__line {
      top: 26px;
    }

    .my-requests-status-progress__step {
      gap: 5px;
    }

    .olreadia-main-grid.is-home .olreadia-choice-stack,
    .olreadia-main-grid.is-home .olreadia-mobile-choice-panel,
    .olreadia-main-grid.is-home .olreadia-nearby-list {
      grid-template-columns: 1fr;
    }

    .olreadia-main-grid.is-home .olreadia-nearby-section {
      padding: 8px;
      border-radius: 16px;
    }

    .olreadia-request-search-heading h2 {
      font-size: 21px;
    }

    .olreadia-radius-row {
      align-items: flex-start;
      flex-direction: column;
    }

    .olreadia-landing-shell {
      width: min(100% - 20px, 1380px);
      padding-top: 12px;
    }

    .olreadia-landing-header {
      gap: 12px;
    }

    .olreadia-welcome-brand {
      width: 100%;
      border-radius: 12px;
    }

    .olreadia-action-row,
    .olreadia-bottom-card .olreadia-action-row {
      width: 100%;
      display: grid;
      grid-template-columns: 1fr;
    }

    .olreadia-side-cta-row {
      grid-template-columns: 1fr;
    }

    .olreadia-public-nav {
      display: grid;
      grid-template-columns: 1fr 1fr;
      width: 100%;
    }

    .olreadia-public-nav button,
    .olreadia-public-nav a {
      justify-content: center;
      width: 100%;
      padding-inline: 8px;
      text-align: center;
    }

    .olreadia-pill-button {
      width: 100%;
    }

    .olreadia-promo-button {
      width: 100%;
    }

    .olreadia-promo-row {
      grid-template-columns: 1fr;
      gap: 10px;
      padding: 12px;
      overflow: hidden;
    }

    .olreadia-promo-cta {
      grid-template-columns: 1fr;
    }

    .olreadia-inline-ads-section .oliveeb-product-ad {
      grid-template-columns: minmax(0, 1fr);
      width: 100%;
      max-width: 100%;
      gap: 6px;
    }

    .olreadia-inline-ads-section .oliveeb-product-ad__icon {
      width: 100%;
      height: 40px;
    }

    .olreadia-inline-ads-section .oliveeb-product-ad__icon img {
      height: 30px;
      max-height: 30px;
    }

    .olreadia-inline-ads-section .oliveeb-product-ad__title {
      font-size: 12px;
    }

    .olreadia-inline-ads-section .oliveeb-product-ad__text {
      font-size: 10px;
      line-height: 1.22;
    }

    .olreadia-subhead-audience {
      align-items: flex-start;
      gap: 8px;
      font-size: 14px;
      line-height: 1.36;
    }

    .olreadia-public-title {
      font-size: 23px;
    }

    .olreadia-public-hero {
      padding: 14px;
    }

    .olreadia-public-grid {
      grid-template-columns: 1fr;
    }

    .olreadia-discovery-heading {
      align-items: flex-start;
      flex-direction: column;
    }

    .olreadia-discovery-heading .olreadia-toggle {
      width: 100%;
      flex-basis: auto;
    }

    .olreadia-section-head {
      flex-direction: column;
    }

    .olreadia-search-grid {
      grid-template-columns: 1fr;
    }

    .olreadia-search-box {
      padding: 12px;
      border-radius: 14px;
    }

    .olreadia-search-field.ant-input-affix-wrapper {
      min-height: 48px;
    }

    .olreadia-search-field.ant-input-affix-wrapper .ant-input,
    .olreadia-search-field.ant-input-affix-wrapper .ant-input::placeholder {
      font-size: 14px;
    }

    .olreadia-about-section {
      padding: 16px;
    }

    .olreadia-about-layout {
      grid-template-columns: 1fr;
    }

    .olreadia-about-grid {
      grid-template-columns: 1fr;
    }

    .olreadia-about-title {
      font-size: 23px;
    }

    .olreadia-about-copy {
      font-size: 17px;
      line-height: 1.55;
    }

    .olreadia-mini-features {
      grid-template-columns: 1fr;
    }

    .olreadia-result-card {
      grid-template-columns: 1fr;
    }

    .olreadia-result-actions {
      width: 100%;
      display: grid;
      grid-template-columns: 1fr;
    }

    .olreadia-job-drawer-bottom-actions {
      display: flex;
      justify-content: flex-end;
    }

    .olreadia-result-card .ant-btn {
      width: 100%;
      min-width: 0 !important;
      justify-self: stretch;
    }

    .olreadia-results-pagination {
      justify-content: center;
      overflow-x: auto;
      padding-bottom: 2px;
    }

    .olreadia-choice-card {
      grid-template-columns: 44px 1fr 18px;
      min-height: 68px;
      padding: 9px 10px;
    }

    .olreadia-choice-icon {
      width: 44px;
      height: 44px;
    }

    .olreadia-choice-title {
      font-size: 16px;
    }

  }
`;

const currentYear = new Date().getFullYear();

const publicNavItems = [
  { key: "home", label: "Home", path: "/" },
  { key: "about", label: "About", path: "/about" },
  { key: "contact", label: "Contact", path: "/contact-us" },
  { key: "privacy", label: "Privacy", path: "/privacy-policy" },
  { key: "help", label: "Help", path: "/help" },
  { key: "terms", label: "Terms", path: "/terms-of-use" },
  { key: "faqs", label: "FAQs", path: "/faqs" },
];

const publicPageContent = {
  about: {
    kicker: "About Olitask",
    title: "Get everyday tasks done with trusted local helpers.",
    deck:
      "Olitask connects people who need help with nearby helpers who can complete errands, home projects, transportation support, and other everyday tasks.",
    body:
      "Post what you need, set a budget and timeframe, review helper offers, and follow the work from request to completion. Helpers can build a profile, choose tasks that match their skills, and earn on a flexible schedule.",
    cards: [
      {
        title: "For requesters",
        text: "Describe the task, compare helper profiles and offers, then choose the right person for the job.",
      },
      {
        title: "For helpers",
        text: "Create a helper profile, showcase your skills, browse local tasks, and earn by completing work well.",
      },
      {
        title: "For peace of mind",
        text: "Use in-app communication, real-time task tracking, reviews, and secure payments from start to finish.",
      },
    ],
  },
  contact: {
    kicker: "Contact Us",
    title: "Talk with Oliveeb about Olitask.",
    deck:
      "Reach out for help with accounts, task requests, helper profiles, payments, safety, or partnerships.",
    body:
      "Olitask is built for people who need reliable help and helpers who want flexible earning opportunities. Contact support if you need help signing in, posting a task, becoming a helper, or understanding payment and safety workflows.",
    cards: [
      {
        title: "Product support",
        text: "Get help with login, account setup, task posting, helper profiles, and app questions.",
      },
      {
        title: "Requester and helper help",
        text: "Find guidance for offers, scheduling, completion, reviews, cancellations, and payments.",
      },
      {
        title: "Partnership questions",
        text: "Ask about community programs, referrals, and shared growth opportunities for Olitask.",
      },
    ],
  },
  privacy: {
    kicker: "Privacy Policy",
    title: "Privacy-aware task help for requesters and helpers.",
    deck:
      "Olitask handles account, profile, payment, location, and task information, so access and account context matter.",
    body:
      "Public pages describe the product at a high level. Detailed privacy obligations, data use, account controls, and service-specific terms should be reviewed with the applicable Oliveeb and Olitask agreements for your account.",
    cards: [
      {
        title: "Account-based access",
        text: "Task details, helper profiles, messages, and payment workflows are intended for authenticated account experiences.",
      },
      {
        title: "Task context",
        text: "Location, schedule, photos, messages, and completion details help requesters and helpers coordinate work.",
      },
      {
        title: "Review terms",
        text: "Use the published Oliveeb privacy and service documents for formal policy details.",
      },
    ],
  },
  help: {
    kicker: "Help",
    title: "Help for using Olitask before and after sign in.",
    deck:
      "Find the right path for account setup, posting tasks, becoming a helper, payments, safety, and app downloads.",
    body:
      "Unlogged-in users can start from the landing page, create an account, download the app, or log in. Signed-in users can manage task requests, helper activity, payments, reviews, and support requests from inside the app.",
    cards: [
      {
        title: "Getting started",
        text: "Use Sign Up to create an account, then choose whether you want to request help, become a helper, or both.",
      },
      {
        title: "Posting a task",
        text: "Describe what you need, set a budget and timeframe, add photos or location details, and review offers.",
      },
      {
        title: "Helping with Olitask",
        text: "Complete your profile, set skills and availability, browse nearby tasks, and make offers.",
      },
    ],
  },
  terms: {
    kicker: "Terms of Use",
    title: "Terms for using Olitask services.",
    deck:
      "Olitask usage is governed by the applicable account, product, payment, and service terms provided by Oliveeb.",
    body:
      "These public pages provide a product overview and starting point. Before using account features, helper verification, payments, task requests, or support workflows, review the applicable terms presented during signup, checkout, or account use.",
    cards: [
      {
        title: "Account responsibility",
        text: "Users are responsible for accurate account, profile, task, location, and payment information.",
      },
      {
        title: "Helper and task workflows",
        text: "Identity, safety, offer, cancellation, dispute, and payment workflows may require additional consent or review.",
      },
      {
        title: "Service updates",
        text: "Olitask features and workflows may evolve as the product and supporting services expand.",
      },
    ],
  },
  faqs: {
    kicker: "FAQs",
    title: "Common questions about Olitask.",
    deck:
      "A quick guide to posting tasks, finding helpers, earning as a helper, payments, safety, and support.",
    body:
      "Olitask helps requesters get practical help quickly and gives helpers a flexible way to earn. The app keeps task details, offers, communication, tracking, payment, and reviews in one place.",
    cards: [
      {
        title: "Do I need an account?",
        text: "Yes. Public pages explain the app, but posting tasks, making offers, messaging, payments, and reviews require an account.",
      },
      {
        title: "How do I post a task?",
        text: "Describe what you need, choose a category, add budget and timing, then review offers from nearby helpers.",
      },
      {
        title: "How do helpers earn?",
        text: "Helpers browse tasks that match their skills and schedule, make offers, complete accepted work, and get paid securely.",
      },
    ],
  },
};

const pageKeyFromPathname = (pathname) => {
  if (pathname === "/" || pathname === "/product") return "home";
  if (pathname.includes("about")) return "about";
  if (pathname.includes("contact")) return "contact";
  if (pathname.includes("privacy")) return "privacy";
  if (pathname.includes("terms")) return "terms";
  if (pathname.includes("faq")) return "faqs";
  if (pathname.includes("help")) return "help";
  return "home";
};

const PublicProductPage = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { user, isAuthenticated } = useAuth();
  const [discoveryContext, setDiscoveryContext] = useState("OPPORTUNITIES");
  const [query, setQuery] = useState("");
  const [activeSearch, setActiveSearch] = useState("");
  const [activeLocation, setActiveLocation] = useState("");
  const [searchRan, setSearchRan] = useState(false);
  const [popularSearchOptions, setPopularSearchOptions] = useState([]);
  const [popularLocationOptions, setPopularLocationOptions] = useState([]);
  const [locationApiSuggestions, setLocationApiSuggestions] = useState([]);
  const [locationApiReady, setLocationApiReady] = useState(false);
  const [professionalRoleOptions, setProfessionalRoleOptions] = useState([]);
  const [jobResults, setJobResults] = useState([]);
  const [talentResults, setTalentResults] = useState([]);
  const [resultsLoading, setResultsLoading] = useState(false);
  const [resultsError, setResultsError] = useState("");
  const [resultsPage, setResultsPage] = useState(1);
  const [resultsTotal, setResultsTotal] = useState(0);
  const [guestSession, setGuestSession] = useState(null);
  const [talentDrawerOpen, setTalentDrawerOpen] = useState(false);
  const [talentDrawerLoading, setTalentDrawerLoading] = useState(false);
  const [selectedTalent, setSelectedTalent] = useState(null);
  const [jobDrawerOpen, setJobDrawerOpen] = useState(false);
  const [jobDrawerLoading, setJobDrawerLoading] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [nearbyTasks, setNearbyTasks] = useState([]);
  const [nearbyTasksLoading, setNearbyTasksLoading] = useState(false);
  const [currentLocationLoading, setCurrentLocationLoading] = useState(false);
  const [nearbyTasksError, setNearbyTasksError] = useState("");
  const [nearbyTasksOpen, setNearbyTasksOpen] = useState(true);
  const [nearbyTasksView, setNearbyTasksView] = useState("MAP");
  const [showAllNearbyTasks, setShowAllNearbyTasks] = useState(false);
  const [landingLocationInput, setLandingLocationInput] = useState("");
  const [landingLocationLabel, setLandingLocationLabel] = useState("");
  const [landingLocationFocused, setLandingLocationFocused] = useState(false);
  const [landingLocationSuggestions, setLandingLocationSuggestions] = useState([]);
  const [landingLocationSuggestionsLoading, setLandingLocationSuggestionsLoading] = useState(false);
  const [appConfigGoogleMapsApiKey, setAppConfigGoogleMapsApiKey] = useState("");
  const [radiusMiles, setRadiusMiles] = useState(DEFAULT_RADIUS_MILES);
  const [guestTaskDrawerOpen, setGuestTaskDrawerOpen] = useState(false);
  const [selectedGuestTask, setSelectedGuestTask] = useState(null);
  const [selectedMapSummaryTask, setSelectedMapSummaryTask] = useState(null);
  const [searchDropdownOpen, setSearchDropdownOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [signInPromptOpen, setSignInPromptOpen] = useState(false);
  const [premiumOpen, setPremiumOpen] = useState(false);
  const discoveryHeadingRef = useRef(null);
  const nearbySectionRef = useRef(null);
  const locationAutocompleteServiceRef = useRef(null);
  const locationFetchSeqRef = useRef(0);
  const landingLocationRef = useRef(null);
  const landingLocationFetchSeqRef = useRef(0);
  const nearbyMapContainerRef = useRef(null);
  const nearbyMapContainerNodeRef = useRef(null);
  const nearbyMapRef = useRef(null);
  const nearbyMapMarkersRef = useRef([]);

  const isTalent = discoveryContext === "TALENT";
  const envGoogleMapsApiKey =
    process.env.REACT_APP_GOOGLE_MAPS_API_KEY ||
    process.env.VITE_GOOGLE_MAPS_API_KEY ||
    "";
  const googleMapsApiKey =
    envGoogleMapsApiKey ||
    appConfigGoogleMapsApiKey ||
    "";
  const actorId = user?.id || user?.userId || guestSession?.userId || guestSession?.sessionId || "";
  const guestAuthToken = isAuthenticated ? "" : bearerGuestToken(guestSession);
  const pageKey = pageKeyFromPathname(pathname);
  const isHomePage = pageKey === "home";
  const publicContent = publicPageContent[pageKey];

  const searchPlaceholder = isTalent
    ? "Search helpers e.g. cleaning, delivery, handyman"
    : "Search tasks e.g. errands, moving, airport help";

  const resultSource = isTalent ? talentResults : jobResults;
  const typedQuery = query.trim();
  const shouldShowResults = searchRan;
  const uniqueNearbyTasks = useMemo(() => dedupeById(nearbyTasks), [nearbyTasks]);
  const visibleNearbyTasks = showAllNearbyTasks ? uniqueNearbyTasks : uniqueNearbyTasks.slice(0, 3);
  const hasLandingSearchLocation = Boolean(landingLocationLabel);
  const mappableNearbyTasks = useMemo(
    () =>
      uniqueNearbyTasks
        .map((task) => ({ task, coords: taskCoordinates(task) }))
        .filter((item) => item.coords),
    [uniqueNearbyTasks],
  );

  useEffect(() => {
    let cancelled = false;

    const initializeGuest = async () => {
      if (isAuthenticated) {
        setGuestSession(null);
        return;
      }
      try {
        const session = await ensureGuestSession();
        if (!cancelled) setGuestSession(session || null);
      } catch {
        if (!cancelled) setGuestSession(null);
      }
    };

    initializeGuest();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated]);

  useEffect(() => {
    if (envGoogleMapsApiKey || appConfigGoogleMapsApiKey) return;

    let cancelled = false;
    const loadAppConfig = async () => {
      try {
        const session = await ensureGuestSession();
        const token = bearerGuestToken(session);
        const response = await apiClient.get("/olitask-service/api/v1/app-config", {
          headers: {
            ...(token ? { Authorization: token } : {}),
            "O-SKIP-WORKSPACE-CONTEXT": "1",
          },
        });
        if (cancelled) return;
        setAppConfigGoogleMapsApiKey(googleMapsKeyFromConfigResponse(response));
      } catch {
        if (!cancelled) setAppConfigGoogleMapsApiKey("");
      }
    };

    loadAppConfig();

    return () => {
      cancelled = true;
    };
  }, [appConfigGoogleMapsApiKey, envGoogleMapsApiKey]);

  useEffect(() => {
    let alive = true;
    setLocationApiReady(false);
    locationAutocompleteServiceRef.current = null;

    ensureGoogleMapsPlacesLoaded(googleMapsApiKey)
      .then((ok) => {
        if (!alive || !ok || !window.google?.maps?.places) return;
        locationAutocompleteServiceRef.current = new window.google.maps.places.AutocompleteService();
        setLocationApiReady(true);
      })
      .catch(() => {
        if (!alive) return;
        setLocationApiReady(false);
      });

    return () => {
      alive = false;
    };
  }, [googleMapsApiKey]);

  useEffect(() => {
    if (
      nearbyTasksView !== "MAP" ||
      !googleMapsApiKey ||
      !locationApiReady ||
      !nearbyMapContainerRef.current ||
      !window.google?.maps
    ) {
      return;
    }

    const searchedCoords = landingLocationRef.current;
    const firstTaskCoords = mappableNearbyTasks[0]?.coords;
    const centerCoords = searchedCoords || firstTaskCoords;
    if (!centerCoords) return;

    const center = {
      lat: centerCoords.latitude,
      lng: centerCoords.longitude,
    };

    const mapContainer = nearbyMapContainerRef.current;
    const shouldCreateMap =
      !nearbyMapRef.current ||
      nearbyMapContainerNodeRef.current !== mapContainer;

    if (shouldCreateMap) {
      nearbyMapContainerNodeRef.current = mapContainer;
      nearbyMapRef.current = new window.google.maps.Map(nearbyMapContainerRef.current, {
        center,
        zoom: 12,
        disableDefaultUI: true,
        zoomControl: true,
        clickableIcons: false,
      });
    } else {
      nearbyMapRef.current.setCenter(center);
    }

    window.requestAnimationFrame(() => {
      if (nearbyMapRef.current && window.google?.maps?.event) {
        window.google.maps.event.trigger(nearbyMapRef.current, "resize");
        nearbyMapRef.current.setCenter(center);
      }
    });

    nearbyMapMarkersRef.current.forEach((marker) => marker.setMap(null));
    nearbyMapMarkersRef.current = [];

    const bounds = new window.google.maps.LatLngBounds();
    bounds.extend(center);

    const searchMarker = new window.google.maps.Marker({
      map: nearbyMapRef.current,
      position: center,
      title: landingLocationLabel || "Search location",
      icon: {
        path: window.google.maps.SymbolPath.CIRCLE,
        scale: 8,
        fillColor: tokens.green,
        fillOpacity: 1,
        strokeColor: "#FFFFFF",
        strokeWeight: 3,
      },
    });
    nearbyMapMarkersRef.current.push(searchMarker);

    const radiusCircle = new window.google.maps.Circle({
      map: nearbyMapRef.current,
      center,
      radius: Number(radiusMiles) * 1609.344,
      strokeColor: tokens.green,
      strokeOpacity: 0.72,
      strokeWeight: 2,
      fillColor: tokens.green,
      fillOpacity: 0.11,
      clickable: false,
    });
    nearbyMapMarkersRef.current.push(radiusCircle);
    const radiusBounds = radiusCircle.getBounds?.();
    if (radiusBounds) {
      bounds.union(radiusBounds);
    }

    mappableNearbyTasks.forEach(({ task, coords }) => {
      const position = { lat: coords.latitude, lng: coords.longitude };
      bounds.extend(position);
      const taskMarkerSvg = encodeURIComponent(`
        <svg xmlns="http://www.w3.org/2000/svg" width="46" height="54" viewBox="0 0 46 54">
          <path d="M23 52C18 44 5 34 5 20.5C5 10.8 13.1 3 23 3s18 7.8 18 17.5C41 34 28 44 23 52Z" fill="${tokens.green}" stroke="#ffffff" stroke-width="4"/>
          <rect x="14" y="14" width="18" height="20" rx="4" fill="#ffffff"/>
          <path d="M18 21.5l3 3 7-7" fill="none" stroke="${tokens.green}" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M18 29h10" stroke="${tokens.darkGreen}" stroke-width="2.4" stroke-linecap="round"/>
        </svg>
      `);
      const marker = new window.google.maps.Marker({
        map: nearbyMapRef.current,
        position,
        title: firstText(task.title, task.name, "Help Request"),
        icon: {
          url: `data:image/svg+xml;charset=UTF-8,${taskMarkerSvg}`,
          scaledSize: new window.google.maps.Size(46, 54),
          anchor: new window.google.maps.Point(23, 52),
        },
      });
      marker.addListener("click", () => setSelectedMapSummaryTask(task));
      nearbyMapMarkersRef.current.push(marker);
    });

    if (mappableNearbyTasks.length > 0 || radiusBounds) {
      nearbyMapRef.current.fitBounds(bounds, 56);
      const minZoom =
        Number(radiusMiles) <= 5 ? 12 :
          Number(radiusMiles) <= 10 ? 11 :
            Number(radiusMiles) <= 20 ? 10 :
              9;
      window.google.maps.event.addListenerOnce(nearbyMapRef.current, "idle", () => {
        const currentZoom = nearbyMapRef.current?.getZoom?.();
        if (Number.isFinite(currentZoom) && currentZoom < minZoom) {
          nearbyMapRef.current.setZoom(minZoom);
        }
      });
    } else {
      const zoomForRadius =
        Number(radiusMiles) <= 5 ? 13 :
          Number(radiusMiles) <= 10 ? 12 :
            Number(radiusMiles) <= 20 ? 11 :
              10;
      nearbyMapRef.current.setZoom(zoomForRadius);
    }
  }, [googleMapsApiKey, landingLocationLabel, locationApiReady, mappableNearbyTasks, nearbyTasksView, radiusMiles]);

  useEffect(() => {
    const value = activeLocation.trim();
    const service = locationAutocompleteServiceRef.current;

    if (!googleMapsApiKey || !locationApiReady || !service || value.length < 2) {
      locationFetchSeqRef.current += 1;
      setLocationApiSuggestions([]);
      return;
    }

    const seq = ++locationFetchSeqRef.current;
    service.getPlacePredictions(
      {
        input: value,
        types: ["(regions)"],
      },
      (predictions) => {
        if (seq !== locationFetchSeqRef.current) return;
        const seen = new Set();
        const next = (Array.isArray(predictions) ? predictions : [])
          .map((item) => String(item?.description || "").trim())
          .filter((item) => {
            const key = item.toLowerCase();
            if (!key || seen.has(key)) return false;
            seen.add(key);
            return true;
          });
        setLocationApiSuggestions(next);
      },
    );
  }, [activeLocation, googleMapsApiKey, locationApiReady]);

  useEffect(() => {
    const value = landingLocationInput.trim();
    const service = locationAutocompleteServiceRef.current;

    if (
      !landingLocationFocused ||
      !googleMapsApiKey ||
      !locationApiReady ||
      !service ||
      value.length < 2 ||
      value.toLowerCase() === "current location"
    ) {
      landingLocationFetchSeqRef.current += 1;
      setLandingLocationSuggestions([]);
      setLandingLocationSuggestionsLoading(false);
      return;
    }

    const seq = ++landingLocationFetchSeqRef.current;
    setLandingLocationSuggestionsLoading(true);
    service.getPlacePredictions(
      {
        input: value,
        types: ["(regions)"],
      },
      (predictions) => {
        if (seq !== landingLocationFetchSeqRef.current) return;
        const seen = new Set();
        const next = (Array.isArray(predictions) ? predictions : [])
          .map((item) => ({
            label: String(item?.description || "").trim(),
            placeId: item?.place_id || item?.description || "",
          }))
          .filter((item) => {
            const key = item.label.toLowerCase();
            if (!key || seen.has(key)) return false;
            seen.add(key);
            return true;
          })
          .slice(0, 6);
        setLandingLocationSuggestions(next);
        setLandingLocationSuggestionsLoading(false);
      },
    );
  }, [googleMapsApiKey, landingLocationFocused, landingLocationInput, locationApiReady]);

  const popularSearchValues = useMemo(
    () => popularSearchOptions.map((item) => item.value),
    [popularSearchOptions],
  );

  const popularLocationValues = useMemo(
    () => popularLocationOptions.map((item) => item.value),
    [popularLocationOptions],
  );

  const searchSuggestions = useMemo(() => {
    return roleOptionsFor(professionalRoleOptions, typedQuery);
  }, [professionalRoleOptions, typedQuery]);

  const locationSuggestions = useMemo(() => {
    const uniqueLocations = uniqueSuggestionValues(popularLocationValues);
    const normalizedLocation = activeLocation.trim().toLowerCase();

    if (!normalizedLocation) {
      return uniqueLocations.slice(0, 8);
    }

    if (googleMapsApiKey && normalizedLocation.length >= 2) {
      return locationApiSuggestions.slice(0, 8);
    }

    const matches = uniqueLocations.filter((item) =>
      item.toLowerCase().includes(normalizedLocation),
    );

    return matches.length > 0 ? matches.slice(0, 8) : uniqueLocations.slice(0, 4);
  }, [activeLocation, googleMapsApiKey, locationApiSuggestions, popularLocationValues]);

  const landingLocationOptions = useMemo(() => {
    const normalizedLocation = landingLocationInput.trim().toLowerCase();
    const googleOptions = landingLocationSuggestions.slice(0, 8).map((item) => ({
            value: item.label,
            label: item.label,
            placeId: item.placeId,
          }));
    const options =
      googleMapsApiKey && normalizedLocation.length >= 2 && googleOptions.length > 0
        ? googleOptions
        : [];

    return [
      { value: "Current Location", label: "Use Current Location" },
      ...options,
    ];
  }, [googleMapsApiKey, landingLocationInput, landingLocationSuggestions]);

  const visibleResults = useMemo(() => {
    if (!shouldShowResults) {
      return [];
    }
    return resultSource;
  }, [resultSource, shouldShowResults]);

  const searchButtonLabel = typedQuery
    ? isTalent
      ? `Search "${typedQuery}"`
      : `Browse "${typedQuery}" tasks`
    : isTalent
      ? "Search Talent"
      : "Browse Current Jobs";

  const goToLogin = () => navigate(paths.simpleLogin);
  const goToSignup = (mode = "GET_HELP") =>
    navigate(paths.getStarted, {
      state: {
        source: "Landing",
        mode: typeof mode === "string" ? mode : "GET_HELP",
      },
    });

  const openAccountPrompt = () => setSignInPromptOpen(true);

  const handleLandingLocationOptionSelect = (value, option = {}) => {
    if (value === "Current Location") {
      setLandingLocationFocused(false);
      setLandingLocationSuggestions([]);
      handleLandingCurrentLocation();
      return;
    }
    selectLandingLocationSuggestion({
      label: value,
      placeId: option?.placeId,
    });
  };

  const fetchLandingNearbyTasks = useCallback(async (coords = landingLocationRef.current, radiusOverride = radiusMiles) => {
    if (!coords?.latitude || !coords?.longitude) {
      setNearbyTasks([]);
      return;
    }

    setNearbyTasksLoading(true);
    setNearbyTasksError("");
    try {
      const clientId = await ensureClientId();
      const session = await ensureGuestSession();
      const token = bearerGuestToken(session);
      if (!token) throw new Error("Unable to get session token.");

      const params = new URLSearchParams({
        page: "0",
        size: String(LANDING_TASK_PAGE_SIZE),
        radius: String(radiusOverride),
        lat: String(coords.latitude),
        lon: String(coords.longitude),
        status: "NEW",
      });

      const response = await fetchWithTimeout(
        `${API_BASE_URL}/olitask-service/api/v1/tasks/near-helper-tasks?${params.toString()}`,
        {
          headers: {
            Authorization: token,
            "O-CLIENT-APP": O_CLIENT_APP,
            [CLIENT_NAME_HEADER_KEY]: CLIENT_NAME_HEADER_VALUE,
            "O-API-KEY": O_API_KEY,
            ...(clientId ? { "O-CLIENT-ID": clientId } : {}),
            "X-API-KEY": O_API_KEY,
          },
        },
        REQUEST_TIMEOUT_MS,
      );

      if (!response.ok) throw new Error("Unable to load nearby requests right now.");
      const data = await response.json();
      setNearbyTasks(normalizeNearbyTasks(data));
      setNearbyTasksOpen(true);
      setNearbyTasksView("MAP");
      setShowAllNearbyTasks(false);
    } catch (nextError) {
      setNearbyTasks([]);
      setNearbyTasksError(nextError?.message || "Unable to load nearby requests right now.");
    } finally {
      setNearbyTasksLoading(false);
    }
  }, [radiusMiles]);

  const handleLandingRadiusChange = useCallback((nextRadius) => {
    setRadiusMiles(nextRadius);
    setNearbyTasksView("MAP");
    if (landingLocationRef.current) {
      fetchLandingNearbyTasks(landingLocationRef.current, nextRadius);
    }
  }, [fetchLandingNearbyTasks]);

  const loadLandingEstimatedLocation = async ({ showWarning = true } = {}) => {
    setNearbyTasksLoading(true);
    setCurrentLocationLoading(true);
    try {
      const { label, coords } = await fetchIpLocationEstimate();
      landingLocationRef.current = coords;
      setLandingLocationInput(label);
      setLandingLocationLabel(`${label} (estimated)`);
      setLandingLocationFocused(false);
      setLandingLocationSuggestions([]);
      setNearbyTasksView("MAP");
      if (showWarning) {
        message.warning("Precise location was not available, so we used your estimated area.");
      }
      await fetchLandingNearbyTasks(coords);
    } catch {
      setNearbyTasksLoading(false);
      setNearbyTasksView("MAP");
      message.warning("Enter a location manually or allow location access.");
    } finally {
      setCurrentLocationLoading(false);
    }
  };

  const handleLandingCurrentLocation = () => {
    if (!navigator.geolocation) {
      loadLandingEstimatedLocation();
      return;
    }

    let locationSettled = false;
    setNearbyTasksLoading(true);
    setCurrentLocationLoading(true);
    setLandingLocationInput("Current Location");
    setLandingLocationLabel("your current location");
    const locationTimer = window.setTimeout(() => {
      if (locationSettled) return;
      locationSettled = true;
      loadLandingEstimatedLocation();
    }, LOCATION_TIMEOUT_MS + 1500);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        if (locationSettled) return;
        locationSettled = true;
        window.clearTimeout(locationTimer);
        const coords = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        };
        landingLocationRef.current = coords;
        setLandingLocationInput("Current Location");
        setLandingLocationLabel("your current location");
        setLandingLocationFocused(false);
        setLandingLocationSuggestions([]);
        setCurrentLocationLoading(false);
        fetchLandingNearbyTasks(coords);
      },
      () => {
        if (locationSettled) return;
        locationSettled = true;
        window.clearTimeout(locationTimer);
        loadLandingEstimatedLocation();
      },
      { enableHighAccuracy: true, timeout: LOCATION_TIMEOUT_MS, maximumAge: 60000 },
    );
  };

  const resolveLandingLocation = async ({ locationText, placeId } = {}) => {
    if (!googleMapsApiKey) throw new Error("Location search is not ready yet.");
    if (placeId) {
      await ensureGoogleMapsPlacesLoaded(googleMapsApiKey);
      if (!window.google?.maps?.places?.PlacesService) {
        throw new Error("Location search is not ready yet.");
      }

      return new Promise((resolve, reject) => {
        const service = new window.google.maps.places.PlacesService(document.createElement("div"));
        service.getDetails(
          {
            placeId,
            fields: ["formatted_address", "geometry", "place_id"],
          },
          (result, status) => {
            const point = result?.geometry?.location;
            if (
              status !== window.google.maps.places.PlacesServiceStatus.OK ||
              !point
            ) {
              reject(new Error("Location not found."));
              return;
            }
            resolve({
              label: result.formatted_address || locationText || "",
              coords: {
                latitude: typeof point.lat === "function" ? point.lat() : Number(point.lat),
                longitude: typeof point.lng === "function" ? point.lng() : Number(point.lng),
              },
            });
          },
        );
      });
    }

    const queryText = String(locationText || "").trim();
    await ensureGoogleMapsPlacesLoaded(googleMapsApiKey);
    if (!window.google?.maps?.Geocoder) {
      throw new Error("Location search is not ready yet.");
    }

    return new Promise((resolve, reject) => {
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ address: queryText }, (results, status) => {
        const result = Array.isArray(results) ? results[0] : null;
        const point = result?.geometry?.location;
        if (status !== "OK" || !point) {
          reject(new Error("Location not found."));
          return;
        }
        resolve({
          label: result.formatted_address || queryText,
          coords: {
            latitude: typeof point.lat === "function" ? point.lat() : Number(point.lat),
            longitude: typeof point.lng === "function" ? point.lng() : Number(point.lng),
          },
        });
      });
    });
  };

  const searchLandingManualLocation = async (locationText = landingLocationInput) => {
    const queryText = String(locationText || "").trim();
    if (!queryText) {
      message.warning("Enter a city, ZIP, or address.");
      return;
    }
    if (!googleMapsApiKey) {
      setLandingLocationLabel(queryText);
      setNearbyTasks([]);
      setNearbyTasksOpen(true);
      message.warning("Location search is not ready yet. Try current location.");
      return;
    }

    setNearbyTasksLoading(true);
    try {
      const { label, coords } = await resolveLandingLocation({ locationText: queryText });
      if (!Number.isFinite(coords.latitude) || !Number.isFinite(coords.longitude)) {
        throw new Error("Location not found.");
      }
      landingLocationRef.current = coords;
      setLandingLocationInput(label || queryText);
      setLandingLocationLabel(label || queryText);
      setLandingLocationFocused(false);
      setLandingLocationSuggestions([]);
      await fetchLandingNearbyTasks(coords);
    } catch (nextError) {
      setNearbyTasks([]);
      setNearbyTasksError(nextError?.message || "Location not found.");
      message.warning(nextError?.message || "Location not found.");
    } finally {
      setNearbyTasksLoading(false);
    }
  };

  const selectLandingLocationSuggestion = (suggestion) => {
    const value = suggestion?.label || "";
    if (!value) return;
    setLandingLocationInput(value);
    setLandingLocationLabel("");
    setLandingLocationFocused(false);
    setLandingLocationSuggestions([]);
    setNearbyTasksLoading(true);
    resolveLandingLocation({ locationText: value, placeId: suggestion.placeId || "" })
      .then(async ({ label, coords }) => {
        if (!Number.isFinite(coords.latitude) || !Number.isFinite(coords.longitude)) {
          throw new Error("Location not found.");
        }
        landingLocationRef.current = coords;
        setLandingLocationInput(label || value);
        setLandingLocationLabel(label || value);
        await fetchLandingNearbyTasks(coords);
      })
      .catch((nextError) => {
        setNearbyTasks([]);
        setNearbyTasksError(nextError?.message || "Location not found.");
        message.warning(nextError?.message || "Location not found.");
      })
      .finally(() => setNearbyTasksLoading(false));
  };

  const handleLandingGooglePlace = async ({ details, prediction } = {}) => {
    const fallbackLabel = prediction?.description || landingLocationInput;
    if (String(fallbackLabel || "").trim().toLowerCase() === "current location") {
      handleLandingCurrentLocation();
      return;
    }
    if (!details) {
      await searchLandingManualLocation(fallbackLabel);
      return;
    }

    const fields = addressFieldsFromPlaceDetails(details);
    const label = details.formatted_address || fallbackLabel;
    const coords = {
      latitude: fields.latitude,
      longitude: fields.longitude,
    };
    if (!Number.isFinite(coords.latitude) || !Number.isFinite(coords.longitude)) {
      await searchLandingManualLocation(label);
      return;
    }

    landingLocationRef.current = coords;
    setLandingLocationInput(label);
    setLandingLocationLabel(label);
    setLandingLocationFocused(false);
    setLandingLocationSuggestions([]);
    await fetchLandingNearbyTasks(coords);
  };

  const focusDiscoveryHeading = () => {
    window.requestAnimationFrame(() => {
      discoveryHeadingRef.current?.scrollIntoView({ block: "start", behavior: "smooth" });
      discoveryHeadingRef.current?.focus({ preventScroll: true });
    });
  };

  const focusNearbyRequests = () => {
    window.requestAnimationFrame(() => {
      nearbySectionRef.current?.scrollIntoView({ block: "start", behavior: "smooth" });
      nearbySectionRef.current?.focus({ preventScroll: true });
    });
  };

  const requestIdentity = async () => {
    let requestActorId = actorId;
    let requestAuthToken = guestAuthToken;

    if (!isAuthenticated && !requestAuthToken) {
      const session = await ensureGuestSession();
      setGuestSession(session || null);
      requestActorId = session?.userId || session?.sessionId || "";
      requestAuthToken = bearerGuestToken(session);
    }

    return { requestActorId, requestAuthToken };
  };

  const loadLandingResults = async (page = 1, totalCap = null) => {
    try {
      const { requestActorId, requestAuthToken } = await requestIdentity();
      if (!isAuthenticated && !requestAuthToken) return;
      setResultsLoading(true);
      setResultsError("");
      setResultsPage(page);

      if (isTalent) {
        const talentPage = await olreadiaService.professionProfiles.searchTalent({
          actorId: requestActorId,
          authToken: requestAuthToken,
          query: [typedQuery, activeLocation.trim()].filter(Boolean).join(" "),
          page: page - 1,
          size: RESULTS_PAGE_SIZE,
        });
        const talentCards = pageContent(talentPage).map(talentCardFromApi).filter((item) => item.id);
        if (page > 1 && talentCards.length === 0) {
          const cappedTotal = (page - 1) * RESULTS_PAGE_SIZE;
          setResultsTotal(cappedTotal);
          await loadLandingResults(page - 1, cappedTotal);
          return;
        }
        setTalentResults(talentCards);
        const nextTotal = renderedPageTotal(talentPage, talentCards, page);
        setResultsTotal(totalCap == null ? nextTotal : Math.min(nextTotal, totalCap));
        return;
      }

      const jobsPage = await olreadiaService.jobOpportunities.listRecent({
        actorId: requestActorId,
        authToken: requestAuthToken,
        search: [typedQuery, activeLocation.trim()].filter(Boolean).join(" "),
        page: page - 1,
        size: RESULTS_PAGE_SIZE,
      });
      const jobCards = pageContent(jobsPage).map(jobCardFromApi).filter((item) => item.id);
      if (page > 1 && jobCards.length === 0) {
        const cappedTotal = (page - 1) * RESULTS_PAGE_SIZE;
        setResultsTotal(cappedTotal);
        await loadLandingResults(page - 1, cappedTotal);
        return;
      }
      setJobResults(jobCards);
      const nextTotal = renderedPageTotal(jobsPage, jobCards, page);
      setResultsTotal(totalCap == null ? nextTotal : Math.min(nextTotal, totalCap));
    } catch (nextError) {
      if (isTalent) {
        setTalentResults([]);
      } else {
        setJobResults([]);
      }
      setResultsTotal(0);
      setResultsError(nextError?.message || "Could not load public results.");
    } finally {
      setResultsLoading(false);
    }
  };

  const handleSearchSubmit = async () => {
    setSearchRan(true);
    await loadLandingResults(1);
    focusDiscoveryHeading();
  };

  const handleResultsPageChange = async (page) => {
    setSearchRan(true);
    await loadLandingResults(page);
  };

  const handleQueryChange = (value) => {
    setQuery(value);
    setActiveSearch("");
    setSearchDropdownOpen(Boolean(value.trim()));
    setResultsPage(1);
    setResultsTotal(0);
    setSearchRan(false);
    setResultsError("");
  };

  const handleLocationChange = (value) => {
    setActiveLocation(value);
    setResultsPage(1);
    setResultsTotal(0);
    setSearchRan(false);
    setResultsError("");
  };

  const handleSuggestion = (value) => {
    const nextValue = activeSearch === value ? "" : value;
    setActiveSearch(nextValue);
    setQuery(nextValue);
    setSearchDropdownOpen(false);
    setResultsPage(1);
    setResultsTotal(0);
    setSearchRan(false);
    setResultsError("");
  };

  const handleLocation = (value) => {
    const nextValue = activeLocation === value ? "" : value;
    setActiveLocation(nextValue);
    setResultsPage(1);
    setResultsTotal(0);
    setSearchRan(false);
    setResultsError("");
  };

  const clearSearch = () => {
    setQuery("");
    setActiveSearch("");
    setActiveLocation("");
    setSearchDropdownOpen(false);
    setResultsPage(1);
    setResultsTotal(0);
    setSearchRan(false);
    setResultsError("");
    setJobResults([]);
    setTalentResults([]);
  };

  const closeTalentDrawer = () => {
    setTalentDrawerOpen(false);
    setSelectedTalent(null);
    setTalentDrawerLoading(false);
  };

  const closeJobDrawer = () => {
    setJobDrawerOpen(false);
    setSelectedJob(null);
    setJobDrawerLoading(false);
  };

  const openTalentDrawer = async (item) => {
    if (!item?.id) return;
    setTalentDrawerOpen(true);
    setTalentDrawerLoading(true);
    setSelectedTalent(item);
    try {
      const { requestAuthToken } = await requestIdentity();
      if (!isAuthenticated && !requestAuthToken) return;
      const fullProfile = await olreadiaService.professionProfiles.get(item.id, requestAuthToken);
      setSelectedTalent(fullProfile || item);
    } catch (nextError) {
      message.error(nextError?.message || "Could not load helper details.");
    } finally {
      setTalentDrawerLoading(false);
    }
  };

  const openJobDrawer = async (item) => {
    if (!item?.id) return;
    setJobDrawerOpen(true);
    setJobDrawerLoading(true);
    setSelectedJob(null);
    try {
      const { requestActorId, requestAuthToken } = await requestIdentity();
      if (!isAuthenticated && !requestAuthToken) return;
      const fullJob = await olreadiaService.jobOpportunities.get(item.id, requestActorId, requestAuthToken);
      setSelectedJob(fullJob);
    } catch (nextError) {
      message.error(nextError?.message || "Could not load task details.");
    } finally {
      setJobDrawerLoading(false);
    }
  };

  const applyFromJobDrawer = () => {
    if (!isAuthenticated) {
      setSignInPromptOpen(true);
      return;
    }
    const jobId = selectedJob?.id || selectedJob?.jobOpportunityId;
    if (jobId) navigate(`${paths.jobApplications}?jobOpportunityId=${encodeURIComponent(jobId)}`);
  };

  const contactFromTalentDrawer = () => {
    setSignInPromptOpen(true);
  };

  const openResultDetails = (item) => {
    if (!item?.id) return;
    if (isTalent) {
      openTalentDrawer(item);
      return;
    }
    openJobDrawer(item);
  };

  const openGuestTaskDrawer = (task) => {
    if (!task?.id && !task?.taskId) return;
    setSelectedGuestTask(task);
    setGuestTaskDrawerOpen(true);
  };

  const openGuestTaskDrawerFromMapSummary = (task) => {
    setSelectedMapSummaryTask(null);
    openGuestTaskDrawer(task);
  };

  const closeGuestTaskDrawer = () => {
    setGuestTaskDrawerOpen(false);
    setSelectedGuestTask(null);
  };

  const loginFromGuestTaskDrawer = () => {
    closeGuestTaskDrawer();
    goToLogin();
  };

  const requireAccountFromGuestTaskDrawer = () => {
    setSignInPromptOpen(true);
  };

  const signupFromGuestTaskDrawer = () => {
    if (!isAuthenticated) {
      setSignInPromptOpen(true);
      return;
    }
    closeGuestTaskDrawer();
    goToSignup("OFFER_HELP");
  };

  const jobDetailsPath = (item) => {
    const jobId = item?.jobOpportunityId || item?.id;
    return jobId ? paths.jobOpportunityDetails(encodeURIComponent(jobId)) : "";
  };

  const openJobDetailsPage = (item) => {
    const path = jobDetailsPath(item);
    if (path) window.open(path, "_blank", "noopener,noreferrer");
  };

  const shareJobDetails = async (item) => {
    const path = jobDetailsPath(item);
    if (!path) {
      message.warning("This task is missing a shareable details link.");
      return;
    }

    const url = `${window.location.origin}${path}`;
    try {
      if (navigator.share) {
        await navigator.share({
          title: item?.title || "Olitask task",
          url,
        });
        return;
      }

      await navigator.clipboard.writeText(url);
      message.success("Job details link copied.");
    } catch (nextError) {
      if (nextError?.name !== "AbortError") {
        message.error(nextError?.message || "Unable to share this task right now.");
      }
    }
  };

  const copyShareLink = async () => {
    try {
      await navigator.clipboard.writeText(APP_SHARE_LINK);
      message.success("Share link copied.");
    } catch {
      message.error("Unable to copy link.");
    }
  };

  const nativeShare = async () => {
    if (!navigator.share) {
      await copyShareLink();
      return;
    }

    try {
      await navigator.share({
        title: "Share Olitask",
        text: APP_SHARE_MESSAGE,
        url: APP_SHARE_LINK,
      });
    } catch (error) {
      if (error?.name !== "AbortError") {
        message.error("Unable to share right now.");
      }
    }
  };

  const closeSignInPrompt = () => setSignInPromptOpen(false);

  return (
    <div className="olreadia-landing">
      <style>{landingCss}</style>
      <div className="olreadia-landing-shell">
        <header className="olreadia-landing-header">
          <button
            type="button"
            className="olreadia-welcome-brand"
            onClick={() => navigate("/")}
            aria-label="Go to Olitask home"
          >
            <img src="/welcome.png" alt="Olitask" />
          </button>

          <div className="olreadia-header-content">
            <nav className="olreadia-public-nav" aria-label="Public pages">
              {publicNavItems.map((item) => (
                <button
                  type="button"
                  key={item.key}
                  className={pageKey === item.key ? "is-active" : ""}
                  onClick={() => navigate(item.path)}
                >
                  {item.label}
                </button>
              ))}
            </nav>

            <Button
              type="button"
              icon={<EnvironmentOutlined />}
              className="olreadia-mobile-nearby-button"
              onClick={focusNearbyRequests}
            >
              View Nearby Task Request
            </Button>

            <div className="olreadia-promo-row is-compact">
              <span className="olreadia-subhead-audience">
                <span className="olreadia-subhead-label">Olitask</span>
                <span>Olitask connects people, families, organizations, and businesses with trusted local helpers for everyday tasks, home care, airport luggage assistance, and community services.</span>
                <Button
                  icon={<ShareAltOutlined />}
                  className="olreadia-header-share-button"
                  onClick={() => setShareOpen(true)}
                >
                  Share
                </Button>
              </span>
              <span className="olreadia-promo-cta">
                <Button
                  icon={<LoginOutlined />}
                  className="olreadia-promo-button"
                  onClick={goToLogin}
                  style={{
                    color: "#FFFFFF",
                    background: tokens.green,
                    borderColor: tokens.green,
                  }}
                >
                  Login
                </Button>
                <Button
                  type="primary"
                  icon={<UserAddOutlined />}
                  className="olreadia-promo-button"
                  onClick={goToSignup}
                  style={{
                    background: tokens.blue,
                    borderColor: tokens.blue,
                  }}
                >
                  Get Started
                </Button>
                <a className="olreadia-download-button" href={DOWNLOAD_PAGE_LINK} aria-label="Download Olitask app">
                  <img src="/app-download.jpg" alt="" />
                </a>
              </span>
              <OliveebProductAds className="pm-right-ads-section pm-right-product-ads-top" intervalMs={4200} compact />
            </div>
          </div>
        </header>

        <main className={`olreadia-main-grid${isHomePage ? " is-home" : ""}`}>
          <section className="olreadia-panel olreadia-discovery">
            {publicContent ? (
              <>
                <p className="olreadia-section-title">Welcome to Olitask App</p>
                <section className="olreadia-public-hero">
                  <span className="olreadia-public-kicker">{publicContent.kicker}</span>
                  <h1 className="olreadia-public-title">{publicContent.title}</h1>
                  <p className="olreadia-public-deck">{publicContent.deck}</p>
                  <p className="olreadia-public-body">{publicContent.body}</p>
                  <div className="olreadia-public-grid">
                    {publicContent.cards.map((card) => (
                      <article className="olreadia-public-card" key={card.title}>
                        <Text className="olreadia-public-card-title">{card.title}</Text>
                        <Text className="olreadia-public-card-text">{card.text}</Text>
                      </article>
                    ))}
                  </div>
                  <div className="olreadia-modal-actions">
                    <Button
                      type="primary"
                      icon={<UserAddOutlined />}
                      size="large"
                      onClick={goToSignup}
                      style={{
                        borderRadius: 999,
                        background: tokens.green,
                        borderColor: tokens.green,
                        fontWeight: 900,
                      }}
                    >
                      Sign Up
                    </Button>
                    <Button
                      icon={<LoginOutlined />}
                      size="large"
                      onClick={goToLogin}
                      style={{
                        borderRadius: 999,
                        color: tokens.blue,
                        borderColor: tokens.blue,
                        fontWeight: 900,
                      }}
                    >
                      Login
                    </Button>
                  </div>
                </section>
              </>
            ) : (
              <>
                <section className="olreadia-nearby-section" aria-label="Nearby help requests" ref={nearbySectionRef} tabIndex={-1}>
                  <div className="olreadia-request-search-heading">
                    <div>
                      <h2>Find nearby requests</h2>
                    </div>
                    <span className="olreadia-request-search-badge">
                      <EnvironmentOutlined />
                      Live request search
                    </span>
                  </div>
                  <div className="olreadia-location-box">
                    <div className="olreadia-location-row">
                      <div className="olreadia-location-input-wrap">
                        <GooglePlacesAutocompleteInput
                          googleMapsApiKey={googleMapsApiKey}
                          placeholder="Search city, ZIP, or address"
                          value={landingLocationInput}
                          onChange={(value) => {
                            setLandingLocationInput(value);
                            setLandingLocationLabel("");
                          }}
                          types={["geocode"]}
                          extraOptions={[{ value: "Current Location", label: "Use Current Location" }]}
                          onResolvePlace={handleLandingGooglePlace}
                        />
                      </div>
                      <Button onClick={handleLandingCurrentLocation} loading={currentLocationLoading}>
                        Use Current Location
                      </Button>
                    </div>
                    {landingLocationLabel ? (
                      <Text className="olreadia-location-active">
                        Showing requests near {landingLocationLabel}
                      </Text>
                    ) : null}
                  </div>

                  {uniqueNearbyTasks.length > 0 || hasLandingSearchLocation ? (
                    <>
                      <div className="olreadia-nearby-view-bar">
                        <span>
                          <strong>
                            {nearbyTasksLoading
                              ? "Loading nearby requests..."
                              : uniqueNearbyTasks.length > 0
                                ? `🔔 ${uniqueNearbyTasks.length} new help request${uniqueNearbyTasks.length === 1 ? "" : "s"} near you`
                                : "No available requests nearby yet"}
                          </strong>
                          <small>Map view is shown first. Switch to list for task cards.</small>
                        </span>
                        <div className="olreadia-nearby-view-toggle" aria-label="Nearby request view">
                          <button
                            type="button"
                            className={nearbyTasksView === "MAP" ? "active" : ""}
                            onClick={() => setNearbyTasksView("MAP")}
                          >
                            Map View
                          </button>
                          <button
                            type="button"
                            className={nearbyTasksView === "LIST" ? "active" : ""}
                            onClick={() => setNearbyTasksView("LIST")}
                          >
                            List View
                          </button>
                        </div>
                      </div>

                      <div className="olreadia-radius-row">
                        <Text>Search Radius</Text>
                        <div className="olreadia-radius-chips">
                          {LANDING_RADIUS_OPTIONS.map((option) => (
                            <button
                              type="button"
                              key={option}
                              className={radiusMiles === option ? "active" : ""}
                              onClick={() => handleLandingRadiusChange(option)}
                            >
                              {option} mi
                            </button>
                          ))}
                        </div>
                      </div>

                      {nearbyTasksView === "MAP" ? (
                        <div className="olreadia-nearby-map">
                          <div ref={nearbyMapContainerRef} className="olreadia-nearby-map-canvas" />
                          {mappableNearbyTasks.length === 0 ? (
                            <div className="olreadia-nearby-map-empty">
                              {uniqueNearbyTasks.length > 0
                                ? "Map location is available, but these requests do not include map coordinates yet."
                                : nearbyTasksError || `No available task requests within ${radiusMiles} miles right now.`}
                            </div>
                          ) : null}
                        </div>
                      ) : (
                        <div className="olreadia-nearby-list">
                          {visibleNearbyTasks.length > 0 ? visibleNearbyTasks.map((task) => (
                            <button
                              type="button"
                              className="olreadia-preview-card"
                              key={task.id || task.taskId}
                              onClick={() => openGuestTaskDrawer(task)}
                            >
                              <img src={taskPhotoOf(task)} alt="" />
                              <span>
                                <strong>{firstText(task.title, task.name, "Help Request")}</strong>
                                <small>{taskDescriptionPreview(task)}</small>
                                <em>📍 {locationSummaryOfTask(task)}</em>
                                <em>
                                  Offered: {amountLabelOfTask(task)}
                                  {distanceLabelOfTask(task) ? ` • ${distanceLabelOfTask(task)}` : ""}
                                </em>
                              </span>
                              <b>›</b>
                            </button>
                          )) : (
                            <div className="olreadia-nearby-empty">
                              <Text strong>Nearby Help Requests</Text>
                              <Text>
                                {nearbyTasksError || `No available task requests within ${radiusMiles} miles right now.`}
                              </Text>
                            </div>
                          )}
                          {uniqueNearbyTasks.length > 3 && !showAllNearbyTasks ? (
                            <Button className="olreadia-view-more" onClick={() => setShowAllNearbyTasks(true)}>
                              View More ({uniqueNearbyTasks.length - 3} more)
                            </Button>
                          ) : null}
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="olreadia-nearby-empty">
                      {nearbyTasksLoading ? (
                        <Skeleton active paragraph={{ rows: 2 }} />
                      ) : (
                        <>
                          <Text strong>Nearby Help Requests</Text>
                          <Text>
                            {nearbyTasksError || `No available task requests within ${radiusMiles} miles right now.`}
                          </Text>
                          <div className="olreadia-radius-chips">
                            {LANDING_RADIUS_OPTIONS.map((option) => (
                              <button
                                type="button"
                                key={option}
                                className={radiusMiles === option ? "active" : ""}
                                onClick={() => handleLandingRadiusChange(option)}
                              >
                                {option} mi
                              </button>
                            ))}
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </section>

                <section className="olreadia-mobile-choice-panel" ref={discoveryHeadingRef} tabIndex={-1}>
                  <button
                    type="button"
                    className="olreadia-choice-card"
                    onClick={() => goToSignup("GET_HELP")}
                  >
                    <span className="olreadia-choice-glow olreadia-choice-glow-green" />
                    <span className="olreadia-choice-icon" aria-hidden="true">✅</span>
                    <span>
                      <Text className="olreadia-choice-title">Request Help Now!</Text>
                      <Text className="olreadia-choice-desc">
                        Post a task, get quick responses, and connect with friendly local helpers.
                      </Text>
                    </span>
                    <span className="olreadia-choice-arrow">›</span>
                  </button>

                  <button
                    type="button"
                    className="olreadia-choice-card blue"
                    onClick={() => goToSignup("OFFER_HELP")}
                  >
                    <span className="olreadia-choice-glow olreadia-choice-glow-blue" />
                    <span className="olreadia-choice-icon" aria-hidden="true">🤝</span>
                    <span>
                      <Text className="olreadia-choice-title">Becoming a Helper</Text>
                      <Text className="olreadia-choice-desc">
                        Support nearby people, earn from simple tasks, and help strengthen your community.
                      </Text>
                    </span>
                    <span className="olreadia-choice-arrow">›</span>
                  </button>

                  <button
                    type="button"
                    className="olreadia-choice-card gold"
                    onClick={() => setPremiumOpen(true)}
                  >
                    <span className="olreadia-choice-glow olreadia-choice-glow-green" />
                    <span className="olreadia-choice-icon">
                      <SafetyCertificateOutlined />
                    </span>
                    <span>
                      <Text className="olreadia-choice-title">Secure payments</Text>
                      <Text className="olreadia-choice-desc">
                        Keep payments in app so requesters and helpers have a clearer completion flow.
                      </Text>
                    </span>
                    <span className="olreadia-choice-arrow">›</span>
                  </button>

                  <div className="olreadia-mini-features" aria-label="Olitask benefits">
                    <div className="olreadia-mini-feature">
                      <span aria-hidden="true">⚡</span>
                      <Text>Quick</Text>
                    </div>
                    <div className="olreadia-mini-feature blue">
                      <EnvironmentOutlined />
                      <Text>Local</Text>
                    </div>
                    <div className="olreadia-mini-feature gold">
                      <span aria-hidden="true">💛</span>
                      <Text>Impact</Text>
                    </div>
                  </div>
                </section>
              </>
            )}
          </section>

        </main>

        <section className="olreadia-about-section" aria-label="About Olitask">
          <div className="olreadia-about-layout">
            <div>
              <span className="olreadia-about-kicker">About Olitask</span>
              <h2 className="olreadia-about-title">A simpler way to get help around you.</h2>
              <p className="olreadia-about-copy">
                Olitask helps requesters and helpers coordinate practical work in one place.
                Requesters can post tasks, compare offers, track progress, and confirm completion.
                Helpers can build a profile, choose the tasks that fit their skills, and earn on
                their own schedule.
              </p>
              <p className="olreadia-about-copy">
                The platform connects task discovery, helper matching, communication, scheduling,
                reviews, and secure payments so everyday help feels easier to arrange.
              </p>
              <div className="olreadia-about-actions">
                <Button
                  type="primary"
                  icon={<UserAddOutlined />}
                  className="olreadia-pill-button"
                  onClick={goToSignup}
                  style={{ background: tokens.green, borderColor: tokens.green }}
                >
                  Get Started
                </Button>
                <Button
                  icon={<LoginOutlined />}
                  className="olreadia-pill-button"
                  onClick={goToLogin}
                  style={{
                    color: tokens.blue,
                    background: tokens.blueSoft,
                    borderColor: "#D7EAFE",
                  }}
                >
                  Login
                </Button>
              </div>
            </div>

            <div>
              <figure className="olreadia-about-media">
                <img src="/team.png" alt="Olitask helpers and requesters" width="1829" height="860" loading="lazy" />
              </figure>
              <div className="olreadia-about-grid">
                <article className="olreadia-about-item">
                  <span className="olreadia-about-icon"><IdcardOutlined /></span>
                  <Text className="olreadia-about-item-title">Helper profiles</Text>
                  <Text className="olreadia-about-item-text">
                    Review skills, ratings, and helper details before choosing an offer.
                  </Text>
                </article>
                <article className="olreadia-about-item is-blue">
                  <span className="olreadia-about-icon"><SearchOutlined /></span>
                  <Text className="olreadia-about-item-title">Search tasks or helpers</Text>
                  <Text className="olreadia-about-item-text">
                    Discover local help requests and available helpers with clearer context.
                  </Text>
                </article>
                <article className="olreadia-about-item is-gold">
                  <span className="olreadia-about-icon"><SafetyCertificateOutlined /></span>
                  <Text className="olreadia-about-item-title">Task tracking</Text>
                  <Text className="olreadia-about-item-text">
                    Keep timing, communication, photos, and progress updates together.
                  </Text>
                </article>
                <article className="olreadia-about-item is-blue">
                  <span className="olreadia-about-icon"><SolutionOutlined /></span>
                  <Text className="olreadia-about-item-title">Secure completion</Text>
                  <Text className="olreadia-about-item-text">
                    Confirm completed work, review the experience, and handle payment in app.
                  </Text>
                </article>
              </div>
            </div>
          </div>
        </section>

        <footer className="olreadia-footer">
          <span>Copyright © {currentYear} Oliveeb LLC. All rights reserved.</span>
          <span>Olitask helps people get tasks done with trusted local helpers.</span>
        </footer>
      </div>

      <Modal
        open={shareOpen}
        onCancel={() => setShareOpen(false)}
        footer={null}
        centered
        closeIcon={<CloseOutlined />}
      >
        <span className="olreadia-modal-badge">Share Olitask</span>
        <h2 className="olreadia-modal-title">Invite others to Olitask</h2>
        <p className="olreadia-modal-desc">
          Help friends post tasks, find trusted local helpers, track progress, and pay securely when work is complete.
        </p>
        <div className="olreadia-share-qr">
          <QRCode value={APP_SHARE_LINK} size={172} color={tokens.darkGreen} bordered={false} />
          <Text className="olreadia-share-qr-label">Scan to open Olitask</Text>
        </div>
        <div className="olreadia-share-link">{APP_SHARE_LINK}</div>
        <div className="olreadia-modal-actions">
          <Button icon={<CopyOutlined />} onClick={copyShareLink}>
            Copy Link
          </Button>
          <Button type="primary" icon={<ShareAltOutlined />} onClick={nativeShare}>
            Share Link
          </Button>
        </div>
      </Modal>

      <JobDetailsDrawer
        open={jobDrawerOpen}
        loading={jobDrawerLoading}
        job={selectedJob}
        onApply={applyFromJobDrawer}
        onOpenDetails={() => openJobDetailsPage(selectedJob)}
        onShare={() => shareJobDetails(selectedJob)}
        showPosterDetails={false}
        onClose={closeJobDrawer}
      />

      <LandingTalentDetailsDrawer
        open={talentDrawerOpen}
        loading={talentDrawerLoading}
        profile={selectedTalent}
        onContact={contactFromTalentDrawer}
        onClose={closeTalentDrawer}
      />

      <GuestTaskDetailsDrawer
        open={guestTaskDrawerOpen}
        task={selectedGuestTask}
        onClose={closeGuestTaskDrawer}
        onLogin={loginFromGuestTaskDrawer}
        onSignup={signupFromGuestTaskDrawer}
        onRequireAccount={requireAccountFromGuestTaskDrawer}
      />

      <GuestMapTaskSummaryModal
        open={Boolean(selectedMapSummaryTask)}
        task={selectedMapSummaryTask}
        onClose={() => setSelectedMapSummaryTask(null)}
        onOpenDetails={openGuestTaskDrawerFromMapSummary}
      />

      <SignInRequiredModal
        open={signInPromptOpen}
        onClose={closeSignInPrompt}
        title={guestTaskDrawerOpen ? "Continue with an account" : isTalent ? "Login to contact helpers" : "Login required"}
        message={
          guestTaskDrawerOpen
            ? "To respond to this task and start helping, please login or create an account."
            : isTalent
              ? "Please log in or create an account to proceed."
              : "Please log in or create an account to continue."
        }
        onLogin={guestTaskDrawerOpen ? loginFromGuestTaskDrawer : undefined}
        onSignup={
          guestTaskDrawerOpen
            ? () =>
                navigate(paths.getStarted, {
                  state: {
                    source: "GuestTaskDetails",
                    mode: "OFFER_HELP",
                  },
                })
            : isTalent
            ? undefined
            : () =>
                navigate(paths.getStarted, {
                  state: {
                    source: "GuestTaskDetails",
                    mode: "OFFER_HELP",
                  },
                })
        }
      />

      <Modal
        open={premiumOpen}
        onCancel={() => setPremiumOpen(false)}
        footer={null}
        centered
        closeIcon={<CloseOutlined />}
      >
        <span
          className="olreadia-modal-badge"
          style={{
            color: tokens.darkRed,
            background: tokens.goldSoft,
            borderColor: tokens.goldBorder,
          }}
        >
          Premium Tools
        </span>
        <h2 className="olreadia-modal-title">Choose a premium option</h2>
        <p className="olreadia-modal-desc">
          Create an account to manage verification upgrades, integrations, and marketplace access.
        </p>
        <div className="olreadia-modal-actions">
          <Button
            type="primary"
            icon={<UserAddOutlined />}
            onClick={() => {
              setPremiumOpen(false);
              goToSignup();
            }}
          >
            Create Account
          </Button>
          <Button
            icon={<EnvironmentOutlined />}
            onClick={() => {
              setPremiumOpen(false);
              openAccountPrompt();
            }}
          >
            Continue as Guest
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default PublicProductPage;
